/**
 * DualCTAVideoLanding — Fullscreen Mux video landing with two CTAs.
 *
 * Renders a fullscreen autoplay video from the premium-invite public config
 * (simple_mux_overlay_playback_id) with:
 *   – Primary CTA  : VIP checkout directo (MP AR / Stripe intl) — ver premiumVipCheckoutDestination
 *   – Secondary CTA: Free Telegram channel (free_telegram_url from config or env fallback)
 *
 * Mobile-first. Fully iOS Safari + Instagram in-app browser safe:
 *   • h-dvh / min-h-dvh (no h-screen)
 *   • WebkitTransform translate3d GPU layer (Instagram WebView fixed-positioning)
 *   • touchAction: none + overscrollBehavior: none (iOS rubber-band prevention)
 *   • Safe area insets via max(fallback, env(safe-area-inset-*))
 *   • 44 px minimum touch targets
 *   • instagram-browser-modal class for height cascade in Meta IAB
 *   • Body position:fixed + overflow:hidden to prevent page scroll bleed-through
 *
 * Route: /video-landing (add to App.tsx as needed; also usable on any slug).
 *
 * Config — pulled from /api/premium-invite-public-config at runtime:
 *   simple_mux_overlay_playback_id  — playback ID for the video
 *   free_telegram_url               — secondary free CTA URL
 *   vip_checkout_base_url           — override CTA VIP (Comunidad admin); DualCTA + Reels lo aplican
 *
 * Env var fallbacks (build-time):
 *   VITE_PREMIUM_MUX_ES_VIP         — playback ID fallback
 *   VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL — free Telegram URL fallback
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import { useToast } from "@/hooks/use-toast";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import {
  DUAL_CTA_TEASER_EXTERNAL_ID,
  DUAL_CTA_TEASER_TITLE,
  getDualCtaTeaserMuxPlaybackIds,
} from "@/lib/muxApi";
import {
  applyInstagramBrowserCSS,
  isInstagramBrowser,
} from "@/lib/instagramBrowserDetection";
import {
  trackViewContentMeta,
  trackInitiateCheckoutMeta,
  trackLeadMeta,
} from "@/lib/metaPixelAnalytics";
import { cn } from "@/lib/utils";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  BRAND_SOURCE_KEY,
  BRAND_DEFAULT_VIP_VIDEO_PREVIEW_IMAGE,
  BRAND_POWERED_BY_STRIPE_URL,
  BRAND_MERCADOPAGO_HANDSHAKE_URL,
} from "@/lib/brandConfig";
import { getLabelsForLanguage } from "@/config/bioTemplatePremiumPlans";
import {
  isArgentinaPaymentContext,
  resolveCheckoutCountryCode,
} from "@/lib/resolveCheckoutCountryCode";
import { prefetchBrandRuntimeConfig } from "@/lib/brandRuntimeConfig";
import { CANONICAL_MUX_PLAYBACK_ID } from "@/lib/canonicalMuxPlayback";
import {
  CANONICAL_VIP_FIRST_MONTH_DISCOUNT_PERCENT,
  CANONICAL_VIP_FIRST_MONTH_PROMO_ID,
  CANONICAL_VIP_PRICE_USD,
  CANONICAL_VIP_STRIPE_PRICE_ID,
  getCanonicalVipPlanFallback,
} from "@/lib/canonicalVipMembership";
import { resolveMuxCtaPlaybackFallback } from "@/lib/runtimeBrandEnv";
import {
  getServerTemplateConfig,
  getWorkerGeoAnalyticsProps,
  normalizeDualCtaFromWorker,
} from "@/lib/serverTemplateDetection";

import { comunidadDualCtaExtraProps } from "@/lib/comunidadPostHog";
import {
  getVipCheckoutGateway,
  hasVipCheckoutCta,
  isPremiumInviteLandingPath,
  resolveDirectVipCheckoutUrl,
  resolvePremiumInviteVipCtaHref,
} from "@/lib/premiumVipCheckoutDestination";
import {
  localeForPlansFromMuxLanguage,
  startVipMembershipVideoCheckout,
} from "@/lib/vipMembershipVideoCheckout";
import { type SupportedLanguage } from "@/config/videoCTAConfig";
import { detectTrafficSource } from "@/lib/detectTrafficSource";
import {
  getSlackFunnelDiagnosticsExtraPostHog,
  getPostHogSlackSessionPropertiesPostHog,
  registerDualCtaExperienceContextPostHog,
  trackStripePayFailurePostHog,
} from "@/lib/posthogAnalytics";
import {
  buildExperienceSnapshot,
  type ExperienceVariantSource,
} from "@/lib/experienceSnapshot";
import { useVideoAbSlot } from "@/lib/useVideoAbSlot";
import { formatDualCtaFirstMonthOffer } from "@/lib/dualCtaPriceDisplay";
import {
  buildDualCtaVipPriceTelemetry,
  type DualCtaVipStripePriceSource,
} from "@/lib/dualCtaVipPriceTelemetry";
import {
  DUAL_CTA_MUX_ANALYTICS_SURFACE,
  DUAL_CTA_RECOVERY_EMAIL_KEY,
  getCheckoutEmailForDualCta,
  markDualCtaConverted,
  mirrorDualCtaEventFromContext,
  persistDualCtaCheckoutEmail,
  postDualCtaHighIntentAbandonment,
  recordDualCtaSessionEnter,
  resolveDualCtaFlow,
  setDualCtaJourneyContext,
  trackDualCtaMuxErrorNoConversion,
} from "@/lib/dualCtaFunnelTracking";
import { isProfileComplete } from "@/lib/auth";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import {
  getClerkTokenImperative,
  getClerkUserImperative,
} from "@/lib/clerkGate";
import { fetchPlanPricing } from "@/lib/planPricingCache";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Embedded Checkout: lazy — @stripe/stripe-js + el modal NO entran al bundle
// inicial de la landing; cargan recién cuando el visitante clickea el CTA VIP
// con el flag activo.
// IMPORTANT: `lazy` must be imported before this const (TDZ). Do not place
// `import { lazy } from "react"` below this line — it freezes the whole SPA
// on brand-loader (Index imports DualCTAVideoLanding statically).
const StripeEmbeddedCheckoutModal = lazy(
  () => import("@/components/StripeEmbeddedCheckoutModal"),
);

/**
 * Flag runtime de PostHog (sin rebuild — las VITE_FF_* son inertes en Railway).
 * Rollout gradual + A/B de conversión embedded vs hosted desde PostHog:
 * https://us.posthog.com/project/422754/feature_flags → "dual-cta-embedded-checkout".
 */
function isEmbeddedCheckoutFlagOn(): boolean {
  if (typeof window === "undefined") return false;
  const ph = (
    window as {
      posthog?: { isFeatureEnabled?: (key: string) => boolean | undefined };
    }
  ).posthog;
  try {
    return ph?.isFeatureEnabled?.("dual-cta-embedded-checkout") === true;
  } catch {
    return false;
  }
}

// Minimal client-side shape for Dual CTA Video Template (resolved server-side or synthesized).
// Matches api/lib/vipVideoAssets DualCtaVideoTemplate (selected fields).
// Coherencia: imagen SIEMPRE la fija que diste; mux_playback_id es el video.
interface DualCtaVideoTemplate {
  id: number;
  site?: string;
  name?: string | null;
  mux_playback_id?: string | null;
  preview_image_url?: string | null;
  price_usd?: number | null;
  discount_percent?: number | null;
  discount_duration_months?: number | null;
  stripe_price_id?: string | null;
  enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

// ── Build-time + Worker runtime fallbacks ───────────────────────────────────
// Country-aware desde 2026-07-02 (antes: "ES" fijo — un visitante DE/US veía el
// primer frame del video español hasta que algo async lo pisara). Seguro en
// module load: el Worker inyecta __TEMPLATE_CONFIG__ como primer <script> del
// <head>, antes del bundle, y resolveCheckoutCountryCode es 100% sincrónico
// (respeta ?checkout_country y test_country de dev). "UNKNOWN" cae a "ES" para
// preservar el comportamiento previo en bots / dev sin Worker. Frozen en module
// load a propósito: no puede cambiar entre renders → nunca remonta el player.
const ENV_PLAYBACK_ID = (() => {
  const cc = resolveCheckoutCountryCode(getServerTemplateConfig()?.country);
  return resolveMuxCtaPlaybackFallback(cc === "UNKNOWN" ? "ES" : cc) || null;
})();

const ENV_FREE_TELEGRAM_URL =
  (import.meta.env.VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL as string | undefined)?.trim() ||
  null;

// NOTA 2026-06-14: antes existía un `ENV_PLAYBACK_ID` separado
// pero — tras la limpieza del literal del fork anterior — quedó leyendo de la
// MISMA env var que ENV_PLAYBACK_ID. Resultado: cuando el playbackId venía del
// env el chequeo de label hits `=== ENV_PLAYBACK_ID` primero y
// el evento Slack reportaba `playback_id_source: "hardcoded"` aunque la fuente
// real era la env. Reportes desde BR exactamente con ese mensaje.
//
// Quitado: ahora la única fuente de fallback estable es ENV_PLAYBACK_ID. Si
// la cadena de fallback del país está vacía, no hay video al primer frame y el
// resolver Mux API (getDualCtaTeaserMuxPlaybackIds) seteará muxTeaserId apenas
// responda. Los labels Slack ahora son honestos: env / cache / admin / mux_api.

// ── Sin localStorage cache (decisión 2026-06-14) ─────────────────────────────
// Antes había un cache de last-good config en localStorage para acelerar el
// primer frame en visitas repetidas. Eliminado porque generaba bugs de
// "veo el video viejo aunque cambien la env Vercel" — el localStorage tapaba
// las nuevas envs hasta que el cache expirara o el user limpiara browser.
//
// Resolver ahora es 100% Vercel env + Mux API en cada visita:
//   initial state: ENV_PLAYBACK_ID || ENV_PLAYBACK_ID (también de env)
//   después fetch: Mux API si encuentra asset taggeado dlfcpo
//
// Trade-off aceptado: visita repetida pierde el ~50-200 ms de "warm start"
// del HLS (renderiza el video apenas el browser parsea el JS bundle, que
// hoy es <100 KB y carga rápido). A cambio: cambio en Vercel impacta al
// instante en TODOS los usuarios sin esperar a TTLs ni a limpieza manual.
type CachedConfig = {
  playbackId: string | null;
  freeTelegramUrl: string | null;
};

// Stubs no-op para mantener el resto del código sin reescribir — el resolver
// trata CACHED_CONFIG=null y writeCachedConfig=no-op como "cache deshabilitado".
function writeCachedConfig(_cfg: CachedConfig): void {
  /* no-op: localStorage cache deshabilitado deliberadamente */
}

function clearCachedPlaybackId(): void {
  /* no-op: nada que limpiar — siempre leemos ENV + Mux API fresh */
}

const CACHED_CONFIG: CachedConfig | null = null;

// ── Analytics ────────────────────────────────────────────────────────────────
// Funnel events for this page, matching the names used cross-brand so existing
// PostHog insights/dashboards keep working. Mux playback quality is already tracked
// separately by MuxVideoPlayerWithCTA via `mux_video_event`; these add the page-level
// conversion funnel (view → start → CTA click) that was otherwise missing here.
type DualCtaEvent =
  | "dual_cta_video_viewed"
  | "dual_cta_video_started"
  | "dual_cta_video_ended"
  | "dual_cta_video_premium_click"
  | "dual_cta_video_free_click"
  | "dual_cta_video_error"
  | "dual_cta_page_exit"
  | "dual_cta_video_stalled"
  | "dual_cta_no_action_timeout"
  | "dual_cta_scroll_attempt"
  | "dual_cta_vip_cta_impression"
  | "dual_cta_video_waiting_for_play"
  | "dual_cta_checkout_initiated"
  | "dual_cta_template_used_in_checkout"
  | "dual_cta_embedded_checkout_opened"
  | "dual_cta_embedded_checkout_closed"
  | "dual_cta_embedded_checkout_fallback";

/** Environment fields that explain soft Mux failures (HTTP / non-secure IAB). */
function getLandingEnvironmentFields(): Record<string, string | number | boolean> {
  if (typeof window === "undefined") return {};
  const protocol = window.location.protocol || "";
  const isSecure =
    typeof window.isSecureContext === "boolean"
      ? window.isSecureContext
      : protocol === "https:";
  return {
    page_protocol: protocol.replace(":", "") || "unknown",
    is_secure_context: isSecure,
    page_origin: window.location.origin || "",
    // True when page is http: — Mux soft-stall + Clerk digest fail in IG IAB
    // (replays 019f7a5f / 019f7a75). Must surface on Slack.
    insecure_page: protocol === "http:" || isSecure === false,
  };
}

/**
 * Worker geo + Admin GeoSim flags for Slack/PostHog.
 * Production truth for country preview is GeoSim (gst mint + Clean VPN),
 * not bare SPA `?country=XX`. See openAdminCountryGeoSimPreview.
 */
function getDualCtaGeoSlackFields(): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  try {
    const geo = getWorkerGeoAnalyticsProps();
    for (const [k, v] of Object.entries(geo)) {
      if (v === undefined || v === null || v === "") continue;
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        out[k] = v;
      }
    }
  } catch {
    /* config not ready */
  }
  // sessionStorage fallback if edge inject hasn't painted adminGeoSim yet
  // but operator already has an active GeoSim session (toolbar).
  if (!out.admin_geo_simulation && typeof sessionStorage !== "undefined") {
    try {
      const raw = sessionStorage.getItem("admin_geo_sim_session");
      if (raw) {
        const s = JSON.parse(raw) as {
          country?: string;
          province?: string;
          city?: string;
          state?: string;
          neighborhood?: string;
          simulateVpn?: string;
        };
        out.admin_geo_simulation = "true";
        if (s.country) out.simulated_country = String(s.country).toUpperCase().slice(0, 2);
        if (s.province) out.simulated_province = String(s.province);
        if (s.city) out.simulated_city = String(s.city);
        if (s.state) out.simulated_state = String(s.state);
        if (s.neighborhood) out.simulated_neighborhood = String(s.neighborhood);
        if (s.simulateVpn) out.simulated_vpn_mode = String(s.simulateVpn);
      }
    } catch {
      /* private mode / bad json */
    }
  }
  return out;
}

function getPageContext() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    page_url: window.location.href,
    referrer: document.referrer || null,
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_content: params.get("utm_content"),
    screen_w: window.screen.width,
    screen_h: window.screen.height,
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    dpr: window.devicePixelRatio ?? null,
    connection_type:
      (
        navigator as Navigator & {
          connection?: { effectiveType?: string };
        }
      ).connection?.effectiveType ?? null,
    ...getLandingEnvironmentFields(),
    ...getDualCtaGeoSlackFields(),
  };
}

/**
 * Disparo real-time a Slack vía /api/track-funnel-failure.
 * Sólo envía kinds que existen en VALID_KINDS del backend
 * (page_enter_dual_cta_landing | cta_click_vip | cta_click_free_telegram |
 *  mux_playback_error). El backend resuelve country desde el header geo si
 * el cliente manda "" (DualCTA es la home mundial sin contexto de país).
 */
function postSlackFunnelDualCta(
  kind:
    | "cta_click_vip"
    | "cta_click_free_telegram"
    | "mux_playback_error"
    | "engagement_dropoff",
  sessionId: string,
  extra: Record<string, string | number | boolean | undefined>,
): void {
  if (typeof window === "undefined") return;
  try {
    // replayNearNow: el deep-link al replay arranca cerca del timestamp actual
    // en vez del inicio absoluto. Útil cuando hay señal accionable AHORA: click
    // (decisión), dropoff (último momento antes de irse), mux error (frame
    // exacto en que rompió). Para page_enter no aplica — el inicio es el evento.
    const replayNearNow =
      kind === "cta_click_vip" ||
      kind === "cta_click_free_telegram" ||
      kind === "engagement_dropoff" ||
      kind === "mux_playback_error";
    const phCtx = getPostHogSlackSessionPropertiesPostHog(
      replayNearNow ? { replayNearNow: true } : undefined,
    );
    const funnelDiag = getSlackFunnelDiagnosticsExtraPostHog(
      replayNearNow ? { replayNearNow: true } : undefined,
    );
    const ts = detectTrafficSource();
    const cleanExtra: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(extra)) {
      if (v === undefined || v === null) continue;
      cleanExtra[k] = v;
    }
    // GeoSim primero: country del Worker simulado gana sobre header real del operador.
    const geoSim = getDualCtaGeoSlackFields();
    void fetch("/api/track-funnel-failure", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        kind,
        template: "DualCTAVideoLanding",
        country: "", // backend → x-vercel-ip-country (Worker ya inyectó sim country en headers path)
        flow_trace_id: sessionId || undefined,
        page_url: window.location.href,
        extra: {
          channel: ts.channel,
          source: ts.source,
          medium: ts.medium,
          in_app_browser: ts.inAppBrowser || undefined,
          ...(ts.raw.referrer_domain && { referrer: ts.raw.referrer_domain }),
          ...geoSim,
          ...cleanExtra,
          ...phCtx,
          ...funnelDiag,
        },
      }),
    }).catch(() => {});
  } catch {
    /* never break UX */
  }
}

/** Latest experience snapshot — filled by DualCTAVideoLanding for every capture. */
let dualCtaExperienceBase: Record<string, string | number | boolean | null> = {
  template: "DualCTAVideoLanding",
  template_type: "DualCTAVideoLanding",
  experience_template: "DualCTAVideoLanding",
  experience_component: "DualCTAVideoLanding",
  product_surface: "dual_cta_home",
  site: BRAND_SOURCE_KEY || "delfuocampo",
  source: BRAND_SOURCE_KEY || "delfuocampo",
};

function setDualCtaExperienceBase(
  snap: Record<string, string | number | boolean | null>,
): void {
  dualCtaExperienceBase = { ...dualCtaExperienceBase, ...snap };
}

function trackDualCta(event: DualCtaEvent, props?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const eventProps: Record<string, unknown> = {
    ...dualCtaExperienceBase,
    template: "DualCTAVideoLanding",
    template_type: "DualCTAVideoLanding",
    experience_template: "DualCTAVideoLanding",
    experience_component: "DualCTAVideoLanding",
    // source for brand isolation in PostHog queries (mirrors server captureBrandEvent)
    source: BRAND_SOURCE_KEY,
    site: BRAND_SOURCE_KEY || "delfuocampo",
    // For identification of which template (new admin-created ones)
    ...( (window as any).__dual_cta_vip_asset_id ? { vip_video_asset_id: (window as any).__dual_cta_vip_asset_id } : {} ),
    ...( (window as any).__dual_cta_mux_playback_id ? { mux_playback_id: (window as any).__dual_cta_mux_playback_id } : {} ),
    ...( (window as any).__dual_cta_template_name ? { template_name: (window as any).__dual_cta_template_name } : {} ),
    ...props,
  };

  // PostHog
  const ph = (
    window as { posthog?: { capture: (e: string, p?: object) => void } }
  ).posthog;
  ph?.capture?.(event, eventProps);

  // GA4 — mirror every dual-CTA event so the funnel is visible in Google Analytics
  const ga = window.gtag;
  if (typeof ga === "function") {
    // Map event names to GA4-recommended names where possible
    const ga4EventName: string =
      event === "dual_cta_video_premium_click" || event === "dual_cta_checkout_initiated"
        ? "begin_checkout"
        : event === "dual_cta_video_free_click"
          ? "select_content"
          : event === "dual_cta_page_exit"
            ? "page_exit"
            : event;

    ga("event", ga4EventName, {
      event_category: "DualCTAVideoLanding",
      event_label: event,
      page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      ...eventProps,
    });

    // GA4 conversion event for VIP click (maps to standard begin_checkout)
    if (event === "dual_cta_video_premium_click") {
      ga("event", "conversion_funnel", {
        event_category: "Conversion",
        event_label: "DualCTA — VIP click",
        funnel_step: "vip_cta_click",
        page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
        ...eventProps,
      });
    }

    // GA4 conversion event for free CTA (espejo del VIP): permite comparar
    // engagement por canal en GA4 sin abrir PostHog. `engagement_time_msec`
    // es el parámetro reservado que GA4 usa nativamente para "Engagement"
    // — incluirlo hace que el click aparezca como sesión engaged en GA4
    // aunque el visitante salga al toque al canal de Telegram externo.
    // `value=0` declara que es conversión sin revenue (vs VIP con ticket).
    if (event === "dual_cta_video_free_click") {
      const propsObj = eventProps;
      const secOnPage = typeof propsObj.seconds_on_page === "number"
        ? (propsObj.seconds_on_page as number)
        : 0;
      ga("event", "conversion_funnel", {
        event_category: "Conversion",
        event_label: "DualCTA — Free Telegram click",
        funnel_step: "free_cta_click",
        page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
        // GA4 engagement: marcar la sesión como engaged al click
        engagement_time_msec: Math.max(1, secOnPage * 1000),
        value: 0,
        currency: "USD",
        ...propsObj,
      });
      // GA4 standard select_content ya se dispara arriba, pero como conversion
      // (custom event en GA4 admin) sólo este `conversion_funnel` con
      // funnel_step explícito es comparable con el VIP del mismo embudo.
    }
  }

  // Force PostHog replay on premium intent — the SDK sampleRate is 0.3 so this
  // is how we still capture 100% of users who reach the highest-intent step.
  if (event === "dual_cta_video_premium_click") {
    const phForceRec = (
      window as { posthog?: { startSessionRecording?: () => void } }
    ).posthog;
    try {
      phForceRec?.startSessionRecording?.();
    } catch {
      /* no-op — replay opt-in is best-effort */
    }
  }

  mirrorDualCtaEventFromContext(event, eventProps);
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function DualCTAVideoLanding({ slotKey = "dual_cta_landing" }: { slotKey?: string } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  // Clerk: SIN hooks acá — la landing está en los paths con provider diferido
  // (perf mobile, ver src/lib/clerkGate.tsx). El estado de sesión se lee
  // imperativamente AL CLICK vía getClerkUserImperative()/getClerkTokenImperative();
  // si clerk-js aún no cargó se trata como signed-out (el default correcto).
  const { toast } = useToast();
  const { setTemplateName } = useCurrentTemplate();
  const { country: workerCountry, language: workerLanguage } = useTemplateSelection();
  // Labels i18n para los CTAs del DualCTA — coherentes con MembresiaPremiumVip /
  // BioTemplatePremium / etc. usando la misma convención "Miembro Exclusivo".
  const L = getLabelsForLanguage(workerLanguage);
  // Normalize Worker language string (e.g. "es-AR", "en-US") to the format
  // MuxVideoPlayerWithCTA accepts. Falls back to "en" for unsupported locales.
  const muxLang = (() => {
    const base = workerLanguage.split("-")[0].toLowerCase();
    const supported: readonly SupportedLanguage[] = ["en", "es", "de", "fr", "it", "pt", "nl", "tr", "ar", "pl", "ja", "ko", "id"];
    return supported.includes(base as SupportedLanguage) ? (base as SupportedLanguage) : "en";
  })();
  const effectiveCountry = resolveCheckoutCountryCode(workerCountry);
  // Config DualCTA por país (mux/label/stripe) coordinada desde /admin/country-template
  // (country_template_config.dual_cta_config → KV → Worker → __TEMPLATE_CONFIG__.dualCta).
  // Override autoritativo: si está seteada, gana sobre la cascada por defecto.
  //
  // Modo preview operador: con ?checkout_country=XX se fetchea la config DualCTA
  // de ESE país del endpoint público y pisa la inyectada — permite ver desde AR
  // exactamente lo que ve un visitante de MX/DE/etc. sin VPN ni debug del Worker.
  const [previewDualCta, setPreviewDualCta] = useState<
    NonNullable<ReturnType<typeof getServerTemplateConfig>>["dualCta"] | null
  >(null);
  const dualCtaCfg = previewDualCta ?? getServerTemplateConfig()?.dualCta;
  const dualCtaCountryMux = dualCtaCfg?.muxPlaybackId?.trim() || "";
  // AR geo-fino (cta-por-región): barrio → ciudad → provincia. Gana al mux de país.
  const dualCtaGeoMux =
    getServerTemplateConfig()?.dualCtaGeo?.muxPlaybackId?.trim() || "";
  const dualCtaGeoMuxSource =
    getServerTemplateConfig()?.dualCtaGeo?.source?.trim() || "";
  // First-month discount + full price display — resolved after slot hook below.
  /** Fuerza re-render cuando /api/public/brand-runtime-config hidrata MUX/MP desde DB. */
  const [brandRuntimeReady, setBrandRuntimeReady] = useState(false);
  /** Fallback Mux country-aware: build Vite → edge HTML → brand-runtime-config API. */
  const brandMuxFallback = useMemo(
    () =>
      resolveMuxCtaPlaybackFallback(effectiveCountry) ||
      ENV_PLAYBACK_ID ||
      null,
    [effectiveCountry, brandRuntimeReady],
  );
  const vipCheckoutGateway = useMemo(
    () => getVipCheckoutGateway(effectiveCountry),
    [effectiveCountry, brandRuntimeReady],
  );
  // Override admin por país (country-template dual_cta.vipHref) gana sobre
  // el override global de Comunidad / premium_invite (vip_checkout_base_url).
  const vipHrefOverride = dualCtaCfg?.vipHref?.trim() || "";
  /** Override global desde /admin/comunidad → premium_invite_config. */
  const [premiumInviteVipBase, setPremiumInviteVipBase] = useState<string | null>(null);
  const primaryCheckoutHref = useMemo(
    () => {
      const cc = (effectiveCountry || "").toUpperCase();
      // dual_cta.vipHref por país: MP solo válido en AR; exterior ignora MP
      // (evita que un vipHref pegado del plan AR contamine GeoSim MX/US/…).
      if (vipHrefOverride) {
        const isMp =
          /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(vipHrefOverride);
        if (cc === "AR" || !isMp) return vipHrefOverride;
      }
      // AR: el CTA VIP de /invitacion-premium SIEMPRE va a VITE_MP_CHECKOUT_VIP_AR
      // (plan 4d4a… $49.999). No Stripe, no Basic. Override comunidad solo si es MP.
      if (cc === "AR") {
        const fromPremium = resolvePremiumInviteVipCtaHref(
          "AR",
          premiumInviteVipBase,
        );
        if (fromPremium && /mercadopago\.com|preapproval_plan_id=/i.test(fromPremium)) {
          return fromPremium;
        }
        return resolveDirectVipCheckoutUrl("AR");
      }
      // Exterior: resolvePremiumInviteVipCtaHref ya descarta vip_checkout_base_url MP.
      // "" → Stripe button (startVipMembershipVideoCheckout).
      const fromPremium = resolvePremiumInviteVipCtaHref(
        effectiveCountry,
        premiumInviteVipBase,
      );
      if (fromPremium) return fromPremium;
      return resolveDirectVipCheckoutUrl(effectiveCountry);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vipHrefOverride, effectiveCountry, brandRuntimeReady, premiumInviteVipBase],
  );
  const showVipCta = useMemo(
    () =>
      // hideVipCta por país (admin) gana sobre la disponibilidad de gateway.
      dualCtaCfg?.hideVipCta !== true &&
      (hasVipCheckoutCta(effectiveCountry) ||
        Boolean(vipHrefOverride) ||
        Boolean(premiumInviteVipBase?.trim())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      effectiveCountry,
      brandRuntimeReady,
      dualCtaCfg?.hideVipCta,
      vipHrefOverride,
      premiumInviteVipBase,
    ],
  );
  const vipCtaDestination =
    primaryCheckoutHref ||
    (vipCheckoutGateway === "stripe_subscription" ? "stripe_subscription_vip" : "");

  /** Trust badge bajo el CTA VIP: Stripe (intl) o Mercado Pago (AR / GeoSim AR / MP href). */
  const arPaymentContext = isArgentinaPaymentContext(
    workerCountry,
    effectiveCountry,
  );
  const isArVipMercadoPago =
    showVipCta &&
    ((effectiveCountry || "").toUpperCase() === "AR" ||
      vipCheckoutGateway === "mercadopago" ||
      arPaymentContext ||
      /mercadopago\.com|preapproval_plan_id=/i.test(primaryCheckoutHref || ""));
  const showMercadoPagoTrustBadge = isArVipMercadoPago;
  const showStripeTrustBadge =
    showVipCta &&
    vipCheckoutGateway === "stripe_subscription" &&
    !showMercadoPagoTrustBadge;

  // ── State: config from API ────────────────────────────────────────────────
  // playbackId: solo el PAS custom del admin (simple_mux_overlay_playback_id).
  // Arranca null — el gate de resolución espera a /api/premium-invite-config
  // antes de montar el player. El mux por país (dualCtaCountryMux) vive fuera
  // de este state y gana en la cadena cuando está seteado.
  // null hasta que /api/premium-invite-config responda — evita montar el player
  // con ENV y luego “pisar” con el PAS custom del admin (flash de otro video).
  const [playbackId, setPlaybackId] = useState<string | null>(
    CACHED_CONFIG?.playbackId || null,
  );
  /** true cuando GET /api/premium-invite-config terminó (éxito o fallo). */
  const [configIsReady, setConfigIsReady] = useState(false);
  // Override admin por país (dual_cta_config): URL Telegram del CTA gratis.
  // Gana sobre config global / cache / env — y el fetch de config no lo pisa.
  const [freeTelegramUrl, setFreeTelegramUrl] = useState<string | null>(
    dualCtaCfg?.freeTelegramUrl || CACHED_CONFIG?.freeTelegramUrl || ENV_FREE_TELEGRAM_URL,
  );
  // Admin toggle: cuando es true muestra el botón gratis junto al VIP.
  // Default false desde 2026-06: la home mundial muestra solo VIP por defecto
  // para forzar engagement de alta intención (el free CTA da una salida fácil
  // que erosiona la conversión). El admin puede activarlo para tests A/B o
  // campañas específicas vía /admin → dual_cta_free_enabled, o forzarlo POR PAÍS
  // vía /admin/country-template → dual_cta_config.freeCtaEnabled.
  const [freeCtaEnabled, setFreeCtaEnabled] = useState<boolean>(
    dualCtaCfg?.freeCtaEnabled ?? false,
  );

  // ── Preview por país (?checkout_country=XX) ───────────────────────────────
  // Herramienta de operador: fetchea la config DualCTA de ese país (fila
  // específica o DEFAULT) del endpoint público y la aplica como si el Worker
  // la hubiera inyectado. Los visitantes normales nunca llevan el param.
  useEffect(() => {
    let cc = "";
    try {
      cc =
        new URLSearchParams(window.location.search)
          .get("checkout_country")
          ?.trim()
          .toUpperCase() ?? "";
    } catch { /* */ }
    if (!/^[A-Z]{2}$/.test(cc)) return;
    fetch(`/api/country-template-config?_t=${Date.now()}`, {
      headers: { "Cache-Control": "no-cache" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          data: {
            entries?: Array<{
              geo_code?: string;
              enabled?: boolean;
              dual_cta_config?: unknown;
            }>;
          } | null,
        ) => {
          if (!data || !Array.isArray(data.entries)) return;
          const byCode = (code: string) =>
            data.entries?.find(
              (e) =>
                e &&
                e.enabled !== false &&
                String(e.geo_code ?? "").toUpperCase() === code,
            );
          const entry = byCode(cc) ?? byCode("DEFAULT");
          const normalized = normalizeDualCtaFromWorker(entry?.dual_cta_config);
          if (!normalized) return;
          setPreviewDualCta(normalized);
          // Estados sembrados con la config inyectada — re-sembrar con el preview.
          if (normalized.freeTelegramUrl) setFreeTelegramUrl(normalized.freeTelegramUrl);
          if (typeof normalized.freeCtaEnabled === "boolean") {
            setFreeCtaEnabled(normalized.freeCtaEnabled);
          }
        },
      )
      .catch(() => { /* preview best-effort */ });
  }, []);
  // Per-language overrides for the 3 CTA labels (vipLabel, freeLabel, loading).
  // Loaded from fetchPremiumInvitePublicConfig → dual_cta_labels_by_lang.
  const [dualCtaOverrides, setDualCtaOverrides] = useState<{ vipLabel?: string; freeLabel?: string; loading?: string } | null>(null);
  // Prioridad: override por PAÍS (/admin/country-template) > override por idioma
  // (dual_cta_labels_by_lang) > default i18n hardcodeado.
  const label = (key: keyof NonNullable<typeof dualCtaOverrides>, fallback: string) => {
    // Variante A/B activa (slot como fuente del video, sin mux por país) → su
    // vip_label pisa el del país. Cerramos sobre slotVariantVipLabel/slotPlaybackId
    // (declarados abajo); label() solo se llama en el render, ya inicializados.
    if (key === "vipLabel" && slotPlaybackId && slotVariantVipLabel) return slotVariantVipLabel;
    if (key === "vipLabel" && dualCtaCfg?.vipLabel) return dualCtaCfg.vipLabel;
    if (key === "freeLabel" && dualCtaCfg?.freeLabel) return dualCtaCfg.freeLabel;
    if (key === "loading" && dualCtaCfg?.loadingLabel) return dualCtaCfg.loadingLabel;
    return dualCtaOverrides?.[key]?.trim() || fallback;
  };
  // Teaser taggeado en Mux (external_id=teaser). Fallback DESPUÉS del PAS custom
  // del admin (simple_mux_overlay_playback_id) y del slot A/B.
  const [muxTeaserId, setMuxTeaserId] = useState<string | null>(null);
  /** true cuando getDualCtaTeaserMuxPlaybackIds terminó (éxito o fallo). */
  const [muxTeaserResolved, setMuxTeaserResolved] = useState(false);
  // Forces the hardcoded fallback when Mux rejects the resolved id post-mount
  // (canonical message: "This URL or playback-id does not exist…"). Without this,
  // a stale Asset ID stored in admin/cache/env keeps failing for every visitor
  // even though we have a known-good asset baked in. One-way switch on purpose:
  // never flips back, so the fallback's own errors can't loop.
  const [forceFallbackPlaybackId, setForceFallbackPlaybackId] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [posterReady, setPosterReady] = useState(false);
  const [checkoutReturnError, setCheckoutReturnError] = useState<string | null>(null);
  /** Feedback breve (sin pulse) si el usuario intenta scrollear (página bloqueada). */
  const [ctaScrollHint, setCtaScrollHint] = useState(false);
  /**
   * Video still buffering (or soft-stall): surface “podés unirte ahora” + soft ring
   * so conversion stays possible while Mux loads (CN/GFW / slow 3G).
   * State was used by effects but useState was dropped → ReferenceError crash (GeoSim).
   */
  const [vipCtaWaitingHint, setVipCtaWaitingHint] = useState(false);

  /** Email local para captura de recovery en Dual CTA (para guests sin Clerk login). */
  const [dualCtaEmail, setDualCtaEmail] = useState("");
  const [dualCtaEmailCaptured, setDualCtaEmailCaptured] = useState(false);
  /** UX free-join recovery: error inline + loading (sin toast ruidoso). */
  const [recoveryEmailError, setRecoveryEmailError] = useState<string | null>(null);
  const [freeJoinBusy, setFreeJoinBusy] = useState(false);
  const recoveryEmailInputRef = useRef<HTMLInputElement | null>(null);

  /** Props de checkout/país en cada evento del embudo — diagnóstico CO/IG sin replay. */
  useEffect(() => {
    let cancelled = false;
    void prefetchBrandRuntimeConfig().finally(() => {
      if (!cancelled) setBrandRuntimeReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!checkoutReturnError) return;
    const t = setTimeout(() => setCheckoutReturnError(null), 8000);
    return () => clearTimeout(t);
  }, [checkoutReturnError]);

  // Load persisted Dual CTA email for recovery
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("ph_recovery_email_v1") || sessionStorage.getItem(DUAL_CTA_RECOVERY_EMAIL_KEY);
      if (stored && stored.includes("@") && !dualCtaEmail) {
        setDualCtaEmail(stored.toLowerCase());
      }
    } catch {}
  }, []);

  // When local email changes, persist for recovery (Dual/Membership context)
  useEffect(() => {
    if (!dualCtaEmail || !dualCtaEmail.includes("@") || dualCtaEmailCaptured) return;
    const { flowName } = resolveDualCtaFlow(location.pathname);
    const effectiveCountry = resolveCheckoutCountryCode(workerCountry);
    persistDualCtaCheckoutEmail({
      email: dualCtaEmail,
      country: effectiveCountry,
      flowName,
      provider: effectiveCountry === "AR" ? "mercadopago" : "stripe",
      amount: getVipPriceTelemetryFields().vip_full_price_usd || 0,
      currency: effectiveCountry === "AR" ? "ARS" : "USD",
      planTier: "vip",
    });
    setDualCtaEmailCaptured(true);
    setDualCtaEmail(dualCtaEmail.toLowerCase());
  }, [dualCtaEmail]);

  // ── Redirect checkout errors (Instagram IAB form POST → cancelUrl) ─────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(location.search);
    const errCode = sp.get("checkout_error")?.trim();
    const errMsg = sp.get("checkout_message")?.trim();
    if (!errCode && !errMsg) return;

    const displayMsg =
      errMsg ||
      (workerLanguage === "en"
        ? "We couldn't start checkout. Please try again."
        : "No pudimos iniciar el pago. Intentá de nuevo.");

    setCheckoutReturnError(displayMsg);
    trackStripePayFailurePostHog({
      stage: "checkout_session_create",
      flow_surface: "dual_cta_video_landing",
      stripe_error_code: errCode || undefined,
      error_message: displayMsg,
      plan_tier: "vip",
      failure_kind: "redirect_return",
    });
    mirrorDualCtaEventFromContext("dual_cta_page_exit", {
      exit_reason: "checkout_return_error",
      checkout_error_code: errCode,
      checkout_error_message: displayMsg,
    });

    try {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("checkout_error");
      clean.searchParams.delete("checkout_message");
      window.history.replaceState({}, "", clean.pathname + clean.search + clean.hash);
    } catch {
      /* best-effort URL cleanup */
    }
  }, [location.search, workerLanguage]);

  // ── Funnel tracking state ─────────────────────────────────────────────────
  const mountTimeRef = useRef<number>(Date.now());
  /** Wall-clock when first `playing` fired (fallback if Mux playhead never reported). */
  const videoStartTimeRef = useRef<number | null>(null);
  /**
   * Max Mux playhead (currentTime) seen via onPlaybackTelemetry.
   * This is the real "how long they watched" signal — not wall-clock
   * (wall-clock overcounts pause / tab background / stalls).
   */
  const maxPlayheadSecondsRef = useRef(0);
  /** Last known media duration from Mux player (seconds). */
  const videoDurationSecondsRef = useRef<number | null>(null);
  const ctaClickedRef = useRef<"premium" | "free" | null>(null);
  /** True cuando Stripe/MP redirect arrancó (no confundir con solo click VIP). */
  const checkoutInitiatedRef = useRef(false);
  const highIntentAbandonmentFiredRef = useRef(false);
  const videoErrorCountRef = useRef(0);
  const stallCountRef = useRef(0);
  const noActionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Soft-stall / never-played already posted as `mux_playback_error`.
   * Exit must NOT also fire `engagement_dropoff` “sin convertir” — that user
   * never saw the video so conversion was impossible (false lead for ops).
   */
  const videoNeverPlayableSlackFiredRef = useRef(false);
  /** Evita doble `dual_cta_page_exit` (visibilitychange + pagehide). */
  const pageExitFiredRef = useRef(false);
  // Scroll: señal de "quiere más info / está confundido". Lo usamos en el
  // mensaje de engagement_dropoff a Slack para distinguir "se fue sin mirar"
  // de "se fue mientras buscaba más info" (la segunda es mejor lead).
  const scrollAttemptedRef = useRef(false);
  // % máximo de scroll alcanzado: granularidad fina sobre "intentó scrollear".
  const maxScrollPctRef = useRef(0);
  // Cuántas veces se fue del tab (visibilitychange → hidden). >= 3 = distraído /
  // comparando precios en otro tab. 0 = atención focalizada.
  const tabBlurCountRef = useRef(0);
  // Tiempo hasta la primera interacción (scroll, click, video play). Marcado
  // una sola vez. < 2 s = engagement inmediato; > 10 s = pasivo antes de actuar.
  const firstInteractionAtRef = useRef<number | null>(null);
  // Session id compartido con BioTemplatePremiumSimple para correlación de embudo.
  const dualCtaSessionIdRef = useRef<string>("");
  // Tracking del playback_id resuelto: cuál salió y de dónde vino (mux_api /
  // admin / cache / env / hardcoded). Sin esto Slack mostraba "video VIP"
  // genérico sin saber qué asset estaba viendo el usuario, ni si el fallback
  // se activó. Determinístico: si el array de Mux contiene
  // ENV_PLAYBACK_ID lo elegimos, sin importar el orden.
  // 2026-06-14: eliminado el label "hardcoded" porque colisionaba con "env"
  // (ambos apuntaban a la misma env var VITE_PREMIUM_MUX_ES_VIP). Inicial
  // "env" si la env está set; "none" si está vacía y todavía no resolvió Mux API.
  // Fuentes honestas para Slack/PostHog. `ab_slot` gana aunque el mux coincida
  // con dualCta país (same-Mux A/B funcional). `country_template` = dualCta KV.
  // `admin` se mantiene como alias legacy de country_template en payloads viejos.
  const playbackIdSourceRef = useRef<
    | "mux_api"
    | "admin"
    | "country_template"
    | "ab_slot"
    | "cache"
    | "env"
    | "none"
  >(ENV_PLAYBACK_ID ? "env" : "none");
  const playbackIdAlternativesRef = useRef<string[]>([]);
  // True desde que el gate de mount del player abrió por primera vez. El teaser
  // de Mux API solo puede ganar la cadena ANTES de esto — un swap post-mount
  // cambia la key del player y produce remount/flash visible.
  const playerMountedRef = useRef(false);
  const [muxPlaybackAlternatives, setMuxPlaybackAlternatives] = useState<
    string[]
  >([]);
  // Playback IDs that already failed (invalid id or demuxer) — rotate through
  // Mux API alternatives before giving up. Typical IG IAB crash:
  // DEMUXER_ERROR_COULD_NOT_PARSE ~20s into pu5Hkn… with no alternate asset.
  const failedPlaybackIdsRef = useRef<Set<string>>(new Set());
  const [playbackIdOverride, setPlaybackIdOverride] = useState<string | null>(
    null,
  );
  // Stable remount counter: only increments on explicit error rotation / force
  // fallback. Prevents the player from remounting (flash) when the async
  // resolution chain (slot, config API, Mux teaser) updates the playback ID
  // after the player already mounted. The key is NOT tied to effectivePlaybackId.
  const [playerRemountKey, setPlayerRemountKey] = useState(0);
  // Embedded Checkout (flag PostHog "dual-cta-embedded-checkout"): client
  // secret de la sesión → monta el modal sobre el video. Los args del último
  // intento quedan en el ref para el fallback hosted si el mount falla.
  const [embeddedCheckoutSecret, setEmbeddedCheckoutSecret] = useState<
    string | null
  >(null);
  const lastVipCheckoutArgsRef = useRef<
    Parameters<typeof startVipMembershipVideoCheckout>[0] | null
  >(null);

  // Unrecoverable after exhausting alternates — poster + CTAs, no frozen frame.
  const [videoBroken, setVideoBroken] = useState(false);
  const videoBrokenRef = useRef(false);
  useEffect(() => {
    videoBrokenRef.current = videoBroken;
  }, [videoBroken]);
  // Last-resort para errores de RED con la cadena agotada (típico: alternativas
  // de Mux API nunca cargaron y el env fallback es el mismo asset que falló —
  // ver PostHog dual_cta_mux_error_no_conversion sin rotación previa). Un único
  // retry diferido de la cadena completa: red móvil que se recupera ≫ asset roto.
  // Acotado a 1 para no loopear; mientras espera, videoBroken deja el CTA visible.
  const networkChainRetryCountRef = useRef(0);
  const networkChainRetryTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (networkChainRetryTimerRef.current !== null) {
        window.clearTimeout(networkChainRetryTimerRef.current);
      }
    },
    [],
  );

  // ── Video A/B slot — admin-assigned playback ID variants (per-country via VideoAbSlotsAdmin) ─────────────────
  // Fetch the "dual_cta_landing" slot from the admin panel.
  // Per-country video + subscriber price/label comes from variants with countries[].
  // Slot A/B: when enabled + country match, wins over country mux so multi-playback
  // A/B per country is measurable. Country mux is force-single when A/B has no match.
  const {
    playbackId: slotPlaybackId,
    variantLabel: slotVariantLabel,
    variantIndex: slotVariantIndex,
    variantStripePriceId: slotVariantStripePriceId,
    variantVipLabel: slotVariantVipLabel,
    variantFullPriceUsd: slotVariantFullPriceUsd,
    variantFirstMonthDiscountPercent: slotVariantFirstMonthDiscountPercent,
    variantVipVideoAssetId: slotVariantVipVideoAssetId,
    isReady: slotIsReady,
    configVersion: slotConfigVersion,
  } = useVideoAbSlot(slotKey, (effectiveCountry && effectiveCountry !== "UNKNOWN") ? effectiveCountry : undefined);

  // Variante A/B activa = slot resolvió una opción publicable.
  // Mismo Mux que dualCta país es válido: la función real es precio/label/peso/cupón.
  const slotIsActiveVariant = Boolean(slotIsReady && slotPlaybackId);
  const [vipPlanFromDashboard, setVipPlanFromDashboard] = useState(false);
  const [vipPlanFallback, setVipPlanFallback] = useState<{
    fullPriceUsd: number;
    stripePriceId: string | null;
    productName: string | null;
  }>(() => getCanonicalVipPlanFallback());
  // Template fields resolved from slot/server (must be declared before price chain).
  const [activeTemplate, setActiveTemplate] = useState<DualCtaVideoTemplate | null>(null);

  // ── Dual CTA Video Template asignado POR PAÍS (dual_cta_config.vipVideoAssetId) ──
  // El admin puede asignar un template completo (mux + precio + descuento + stripe)
  // a un país desde /admin/country-template. El Worker inyecta solo el ID; acá lo
  // resolvemos en runtime vía /api/dual-cta-template. Independiente del A/B slot: el
  // slot resuelve su template en el server (useVideoAbSlot); este es el del país.
  const countryVipVideoAssetId = dualCtaCfg?.vipVideoAssetId ?? null;
  const [countryTemplate, setCountryTemplate] = useState<DualCtaVideoTemplate | null>(null);
  // false = todavía esperando el fetch (bloquea el mount del player → sin flash).
  // Arranca resuelto cuando el país no tiene template asignado (nada que esperar).
  const [countryTemplateResolved, setCountryTemplateResolved] = useState<boolean>(
    !countryVipVideoAssetId,
  );
  useEffect(() => {
    if (!countryVipVideoAssetId) {
      setCountryTemplate(null);
      setCountryTemplateResolved(true);
      return;
    }
    let cancelled = false;
    setCountryTemplateResolved(false);
    fetch(`/api/dual-cta-template?id=${countryVipVideoAssetId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { template?: DualCtaVideoTemplate } | null) => {
        if (cancelled) return;
        setCountryTemplate(data?.template ?? null);
      })
      .catch(() => {
        if (!cancelled) setCountryTemplate(null);
      })
      .finally(() => {
        // Resuelto (éxito o fallo) → si falló, el landing cae a la cascada normal.
        if (!cancelled) setCountryTemplateResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [countryVipVideoAssetId]);

  // Valores efectivos del template del país, con DIRECT-WINS: si el país tiene el
  // campo directo seteado (muxPlaybackId/stripePriceId/…), ese gana y el del
  // template no se usa (queda vacío para caer en la cascada de siempre).
  const ctMux = !dualCtaCountryMux ? countryTemplate?.mux_playback_id?.trim() || "" : "";
  const ctStripe = !dualCtaCfg?.stripePriceId
    ? countryTemplate?.stripe_price_id?.trim() || ""
    : "";
  const ctFullUsd =
    dualCtaCfg?.fullPriceUsd == null ? (countryTemplate?.price_usd ?? undefined) : undefined;
  const ctDiscount =
    dualCtaCfg?.firstMonthDiscountPercent == null
      ? (countryTemplate?.discount_percent ?? undefined)
      : undefined;

  // El país tiene un Dual CTA Video Template asignado y todavía no lo resolvimos:
  // bloquear el mount hasta que el fetch termine, así el player NUNCA monta con el
  // fallback para después pisarlo con el mux del template (= flash). Si el fetch
  // falla, countryTemplateResolved queda true y caemos a la cascada normal.
  // Declarado acá (junto a sus deps) porque el funnel "viewed" también lo usa.
  const pendingCountryTemplate =
    Boolean(countryVipVideoAssetId) && !dualCtaCountryMux && !countryTemplateResolved;

  // Exterior VIP: SIEMPRE price_1TtZs… (prod_UtMb…) para TODAS las variants.
  // A/B solo cambia video/copy — el checkout Stripe no usa price_ de slot/template.
  // AR no usa Stripe (MercadoPago); el id canónico solo afecta copy USD de fallback.
  const isExteriorDualCta = effectiveCountry !== "AR";
  const activeVipStripePriceId = isExteriorDualCta
    ? CANONICAL_VIP_STRIPE_PRICE_ID
    : (activeTemplate?.stripe_price_id) ||
      (slotIsActiveVariant && slotVariantStripePriceId) ||
      dualCtaCfg?.stripePriceId ||
      ctStripe ||
      vipPlanFallback?.stripePriceId ||
      CANONICAL_VIP_STRIPE_PRICE_ID;

  // Cascada video: geo AR fino > slot A/B > país dualCta > country-template > fallback
  const activePlaybackId =
    dualCtaGeoMux ||
    activeTemplate?.mux_playback_id ||
    (slotIsActiveVariant ? slotPlaybackId : null) ||
    dualCtaCountryMux ||
    ctMux ||
    brandMuxFallback;

  const activeVipVideoAssetId = slotIsActiveVariant ? slotVariantVipVideoAssetId : (dualCtaCfg?.vipVideoAssetId ?? null);

  // Expose for tracking functions so all dual_cta events carry the template ID for identification in PostHog / Slack reports
  if (typeof window !== "undefined") {
    (window as any).__dual_cta_vip_asset_id = activeVipVideoAssetId || null;
    (window as any).__dual_cta_mux_playback_id = activePlaybackId || null;
    (window as any).__dual_cta_template_name = activeTemplate?.name || countryTemplate?.name || null;
    // Debug / e2e: variante real del slot en el init (mismo Mux sigue siendo A/B funcional)
    (window as any).__dual_cta_slot_ready = slotIsReady;
    (window as any).__dual_cta_variant_index = slotIsActiveVariant ? slotVariantIndex : -1;
    (window as any).__dual_cta_variant_label = slotIsActiveVariant ? slotVariantLabel || null : null;
    (window as any).__dual_cta_variant_stripe_price_id = slotIsActiveVariant
      ? slotVariantStripePriceId || null
      : null;
  }

  // Derive activeTemplate locally from the enriched slot response (server resolved the template).
  // The useVideoAbSlot already surfaces variant* fields that were populated from template on server.
  useEffect(() => {
    if (!activeVipVideoAssetId) {
      setActiveTemplate(null);
      return;
    }
    // Build a minimal template shape from slot-provided resolved fields (enriched by server)
    // so that downstream code using activeTemplate.* continues to work unchanged.
    // Prefer explicit slot data (which is the enriched one).
    const t: DualCtaVideoTemplate | null = slotIsActiveVariant && slotPlaybackId
      ? {
          id: activeVipVideoAssetId,
          site: BRAND_SOURCE_KEY,
          name: "Slot Template",
          mux_playback_id: slotPlaybackId,
          // imagen SIEMPRE BRAND_DEFAULT_VIP_VIDEO_PREVIEW_IMAGE de brandConfig (coherencia)
          preview_image_url: BRAND_DEFAULT_VIP_VIDEO_PREVIEW_IMAGE,
          stripe_price_id: slotVariantStripePriceId || undefined,
          price_usd: slotVariantFullPriceUsd ?? undefined,
          discount_percent: slotVariantFirstMonthDiscountPercent ?? undefined,
          enabled: true,
          created_at: "",
          updated_at: "",
        }
      : null;
    setActiveTemplate(t);
  }, [activeVipVideoAssetId, slotIsActiveVariant, slotPlaybackId, slotVariantStripePriceId, slotVariantFullPriceUsd, slotVariantFirstMonthDiscountPercent]);
  // Exterior: 50% primer mes fijo (promo_1Txbe… en checkout). A/B no pisa el % ni el full $.
  // AR: display local si admin lo setea (MP no usa este price_ Stripe).
  const activeFirstMonthDiscountPercent = isExteriorDualCta
    ? CANONICAL_VIP_FIRST_MONTH_DISCOUNT_PERCENT
    : (slotIsActiveVariant && slotVariantFirstMonthDiscountPercent) ||
      dualCtaCfg?.firstMonthDiscountPercent ||
      ctDiscount;
  const activeFullPriceUsd = isExteriorDualCta
    ? CANONICAL_VIP_PRICE_USD
    : (slotIsActiveVariant && slotVariantFullPriceUsd) || dualCtaCfg?.fullPriceUsd || ctFullUsd;
  useEffect(() => {
    let cancelled = false;
    // Fallback canónico de DualCTA: tier VIP del dashboard de planes.
    // El operador lo edita en /dashboard/subscripciones/planes.
    // fetchPlanPricing dedupea con el fetch gemelo de MuxVideoPlayerWithCTA
    // (antes: dos requests idénticos en el arranque — replay 019f6c0a…).
    fetchPlanPricing("vip")
      .then((data) => {
        if (cancelled) return null;
        return data;
      })
      .then((data) => {
        if (!data) return;
        if (String(data.currency ?? "usd").trim().toLowerCase() !== "usd") return;
        const cents = Number(data.amount_cents ?? 0);
        if (!Number.isFinite(cents) || cents <= 0) return;
        const priceId =
          typeof data.stripe_price_id === "string" && data.stripe_price_id.startsWith("price_")
            ? data.stripe_price_id
            : null;
        setVipPlanFallback({
          fullPriceUsd: Math.round(cents) / 100,
          stripePriceId: priceId,
          productName:
            typeof data.product_name === "string" && data.product_name.trim().length > 0
              ? data.product_name.trim()
              : null,
        });
        setVipPlanFromDashboard(true);
      })
      .catch(() => {
        /* canonical fallback already in state */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const vipPriceOffer = useMemo(() => {
    const full = activeFullPriceUsd || vipPlanFallback?.fullPriceUsd;
    const disc = activeFirstMonthDiscountPercent;
    if (!full || !disc || disc <= 0) return null;
    return formatDualCtaFirstMonthOffer(full, disc);
  }, [activeFullPriceUsd, vipPlanFallback, activeFirstMonthDiscountPercent]);

  const vipStripePriceSource = useMemo((): DualCtaVipStripePriceSource => {
    if (slotIsActiveVariant && slotVariantStripePriceId) return "ab_slot";
    if (dualCtaCfg?.stripePriceId) return "country_admin";
    if (vipPlanFromDashboard) return "dashboard";
    if (activeVipStripePriceId === CANONICAL_VIP_STRIPE_PRICE_ID) return "canonical";
    if (activeVipStripePriceId) return "dashboard";
    return "server_lookup";
  }, [
    slotIsActiveVariant,
    slotVariantStripePriceId,
    dualCtaCfg?.stripePriceId,
    vipPlanFromDashboard,
    activeVipStripePriceId,
  ]);

  const getVipPriceTelemetryFields = useCallback(
    (): Record<string, string | number> =>
      buildDualCtaVipPriceTelemetry({
        stripePriceId: activeVipStripePriceId,
        priceSource: vipStripePriceSource,
        fullPriceUsd: activeFullPriceUsd || vipPlanFallback?.fullPriceUsd,
        firstMonthDiscountPercent: activeFirstMonthDiscountPercent,
      }),
    [
      activeVipStripePriceId,
      vipStripePriceSource,
      activeFullPriceUsd,
      vipPlanFallback?.fullPriceUsd,
      activeFirstMonthDiscountPercent,
    ],
  );

  const getDualCtaCheckoutDiagnostics = useCallback(
    (): Record<string, string | number | boolean> => ({
      show_vip_cta: showVipCta,
      effective_country: effectiveCountry,
      worker_country: workerCountry || "UNKNOWN",
      vip_checkout_gateway: vipCheckoutGateway,
      vip_cta_destination: vipCtaDestination,
      free_cta_enabled: freeCtaEnabled,
      vip_cta_is_stripe_button: vipCheckoutGateway === "stripe_subscription",
      ...getVipPriceTelemetryFields(),
    }),
    [
      showVipCta,
      effectiveCountry,
      workerCountry,
      vipCheckoutGateway,
      vipCtaDestination,
      freeCtaEnabled,
      getVipPriceTelemetryFields,
    ],
  );

  // ── Instagram / embedded social browser detection ─────────────────────────
  // Sync on first paint — useEffect would miss IG CSS/classes for ~1 frame and
  // under-detect Meta IAB when UA looks like desktop Chrome + fbclid referrer.
  const [embeddedSocialBrowser] = useState(() => isInstagramBrowser());
  const didApplyIgCssRef = useRef(false);

  useEffect(() => {
    if (!embeddedSocialBrowser) return;
    if (!didApplyIgCssRef.current) {
      didApplyIgCssRef.current = true;
      applyInstagramBrowserCSS();
    }
  }, [embeddedSocialBrowser]);

  // ── Template context ──────────────────────────────────────────────────────
  useEffect(() => {
    setTemplateName("DualCTAVideoLanding");
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    setDualCtaJourneyContext({
      pathname: location.pathname,
      country: effectiveCountry,
    });
  }, [location.pathname, effectiveCountry]);

  // ── Body lock: prevent scroll bleed-through on iOS / Android ─────────────
  useEffect(() => {
    const saved = {
      overflow: document.body.style.overflow,
      height: document.body.style.height,
      position: document.body.style.position,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = saved.overflow;
      document.body.style.height = saved.height;
      document.body.style.position = saved.position;
      document.body.style.width = saved.width;
    };
  }, []);

  // ── Primary source: the teaser asset tagged in Mux (external_id=teaser) ────
  // Selección determinística cuando Mux devuelve N assets con el mismo
  // external_id. Orden de preferencia entre los devueltos:
  //   1. ENV_PLAYBACK_ID (VITE_PREMIUM_MUX_ES_VIP) — el operador manda desde
  //      Vercel. Si el ID de la env aparece en la lista de Mux, gana.
  //   2. ENV_PLAYBACK_ID — garantía de estabilidad para
  //      visitas legacy cuando la env está vacía.
  //   3. cleaned[0] — primer asset que Mux devolvió (último recurso).
  // Los alternativos quedan en `playbackIdAlternativesRef` para que el
  // operador los vea en Slack/PostHog.
  useEffect(() => {
    getDualCtaTeaserMuxPlaybackIds()
      .then((ids) => {
        const cleaned = ids.map((x) => x?.trim()).filter(Boolean) as string[];
        if (cleaned.length === 0) return; // no tagged asset → keep fallback chain
        // Las alternativas SIEMPRE entran al pool de rotación (recovery por
        // demuxer-error en IG IAB), gane o no el teaser la cadena de resolución.
        playbackIdAlternativesRef.current = cleaned;
        setMuxPlaybackAlternatives(cleaned);
        // El teaser solo puede ganar la cadena ANTES del mount del player: un
        // swap posterior cambia la key → remount/flash visible. Post-mount el
        // usuario se queda con el seed (precio aceptado de "cero flash").
        if (playerMountedRef.current) return;
        const igMode = isInstagramBrowser();
        // IG IAB: skip env id when other tagged assets exist — pu5Hkn (env) often
        // triggers DEMUXER_ERROR_COULD_NOT_PARSE ~20s in Meta WebView; try siblings first.
        const id =
          igMode && cleaned.length > 1
            ? cleaned.find((x) => x !== ENV_PLAYBACK_ID) ?? cleaned[0]
            : ENV_PLAYBACK_ID && cleaned.includes(ENV_PLAYBACK_ID)
              ? ENV_PLAYBACK_ID
              : cleaned[0];
        playbackIdSourceRef.current = "mux_api";
        setMuxTeaserId(id);
        // Persist so the next visit seeds the player instantly with the teaser.
        writeCachedConfig({
          playbackId: id,
          freeTelegramUrl: CACHED_CONFIG?.freeTelegramUrl ?? ENV_FREE_TELEGRAM_URL,
        });
      })
      .catch(() => {
        /* non-fatal: keep config/cache/env/fallback chain */
      })
      .finally(() => {
        setMuxTeaserResolved(true);
      });
  }, []);

  // ── Fetch dynamic config from API ─────────────────────────────────────────
  useEffect(() => {
    fetchPremiumInvitePublicConfig()
      .then((cfg) => {
        if (!cfg) return;

        const pid =
          cfg.simple_mux_overlay_playback_id?.trim() || null;
        // El mux por país (dual_cta_config.muxPlaybackId) gana sobre el global:
        // no pisar el state — contaminaba telemetría (playback_id_at_mount) y el
        // pool de rotación con un video que este país nunca muestra. Mismo guard
        // que free_telegram_url / dual_cta_free_enabled más abajo.
        // FIX flash: NO pisar el playbackId si el player ya montó — cambiaba
        // effectivePlaybackId → key swap → remount → flash visible. El teaser
        // de Mux API ya tiene este guard (playerMountedRef); aplicamos el mismo
        // principio acá. El admin debe usar dual_cta_config.muxPlaybackId por
        // país para overrides de video, no el overlay global post-mount.
        if (pid && !dualCtaCountryMux && !playerMountedRef.current) {
          setPlaybackId(pid);
          // Solo upgrade el source si el actual es menos prioritario.
          // Mux API > admin > cache > env > none. Admin pisa env/none.
          if (
            playbackIdSourceRef.current === "env" ||
            playbackIdSourceRef.current === "none"
          ) {
            playbackIdSourceRef.current = "admin";
          }
        }

        const ftUrl = cfg.free_telegram_url?.trim() || null;
        // El override por país (dual_cta_config.freeTelegramUrl) gana sobre la global.
        if (ftUrl && !dualCtaCfg?.freeTelegramUrl) setFreeTelegramUrl(ftUrl);

        // Override checkout VIP desde /admin/comunidad (vip_checkout_base_url).
        // País dual_cta.vipHref sigue ganando en primaryCheckoutHref.
        setPremiumInviteVipBase(cfg.vip_checkout_base_url?.trim() || null);

        // Admin: dual_cta_free_enabled = false → solo VIP visible.
        // Si hay override por país (freeCtaEnabled true/false), ese manda.
        if (
          typeof cfg.dual_cta_free_enabled === "boolean" &&
          typeof dualCtaCfg?.freeCtaEnabled !== "boolean"
        ) {
          setFreeCtaEnabled(cfg.dual_cta_free_enabled);
        }

        // Per-language CTA label overrides for this visitor's language.
        if (cfg.dual_cta_labels_by_lang) {
          try {
            const byLang = JSON.parse(cfg.dual_cta_labels_by_lang) as Record<string, { vipLabel?: string; freeLabel?: string; loading?: string }>;
            const overrides = byLang[muxLang] ?? byLang[muxLang.split("-")[0]] ?? null;
            if (overrides) setDualCtaOverrides(overrides);
          } catch { /* non-fatal */ }
        }

        // Persist last-good values so the next visit seeds the player instantly.
        writeCachedConfig({
          playbackId: pid ?? CACHED_CONFIG?.playbackId ?? ENV_PLAYBACK_ID,
          freeTelegramUrl:
            ftUrl ?? CACHED_CONFIG?.freeTelegramUrl ?? ENV_FREE_TELEGRAM_URL,
        });
      })
      .catch(() => {
        // Non-fatal: keep build-time fallbacks
      })
      .finally(() => {
        setConfigIsReady(true);
      });
  }, [muxLang]);

  // ── Funnel: page viewed (fire once, after A/B slot resolves) ─────────────
  // Waits for slotIsReady so abTrackingFields are always populated when the
  // admin has configured variants. Max wait: 3 s (FETCH_TIMEOUT_MS in hook).
  // On slot failure the hook sets isReady=true with playbackId=null, so the
  // event always fires within 3 s — never missed for legitimate page views.
  // Con mux por país el slot no puede ganar → el funnel arranca al instante.
  const viewedFiredRef = useRef(false);
  useEffect(() => {
    // Con template de país sin resolver, esperar: el ID final aún no está fijo.
    if (pendingCountryTemplate) return;
    // Same gate as player: wait for slot so variant fields are real on first event.
    const resolutionReady =
      slotIsReady &&
      (Boolean(slotPlaybackId) ||
        Boolean(dualCtaCountryMux) ||
        Boolean(ctMux) ||
        (configIsReady && muxTeaserResolved));
    if (!resolutionReady) return;
    if (viewedFiredRef.current) return;
    viewedFiredRef.current = true;
    trackDualCta("dual_cta_video_viewed", {
      has_playback_id_at_mount: Boolean(
        slotPlaybackId || dualCtaCountryMux || ctMux || playbackId || muxTeaserId,
      ),
      using_fallback_id: !slotPlaybackId && !dualCtaCountryMux && !ctMux && !playbackId,
      has_free_telegram: freeCtaEnabled && Boolean(freeTelegramUrl),
      embedded_social_browser: embeddedSocialBrowser,
      slot_is_ready: slotIsReady,
      // DualCTA CTAs (VIP / free TG) work without the teaser playing.
      conversion_possible: true,
      ...getDualCtaCheckoutDiagnostics(),
      ...abTrackingFields,
      ...getPageContext(),
    });

    // Real-time webhook: qué template + mux ve el usuario (coherencia).
    // Server → Slack (dedup) + PostHog landing_experience_realtime.
    try {
      const workerMux = (dualCtaCountryMux || ctMux || "").trim();
      // Misma cascada que baseResolvedPlaybackId: slot (variante real) primero.
      const effectiveId = (
        slotPlaybackId ||
        dualCtaCountryMux ||
        ctMux ||
        playbackId ||
        muxTeaserId ||
        brandMuxFallback ||
        ENV_PLAYBACK_ID ||
        CANONICAL_MUX_PLAYBACK_ID ||
        ""
      ).trim();
      const denyPrefix = "KcpwA0200";
      const coherent =
        Boolean(effectiveId) &&
        !effectiveId.includes(denyPrefix) &&
        (!workerMux || workerMux === effectiveId);
      const ts = detectTrafficSource();
      void fetch("/api/track-funnel-failure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          kind: "landing_experience_resolved",
          template: "DualCTAVideoLanding",
          country: "",
          flow_trace_id: dualCtaSessionIdRef.current || undefined,
          page_url: window.location.href,
          extra: {
            worker_template: "DualCTAVideoLanding",
            effective_template: "DualCTAVideoLanding",
            worker_mux_playback_id: workerMux || undefined,
            playback_id: effectiveId || undefined,
            playback_id_source: playbackIdSourceRef.current || undefined,
            playback_id_at_mount: effectiveId || undefined,
            coherence_ok: coherent,
            entry_path: location.pathname,
            entry_channel: ts.channel,
            channel: ts.channel,
            source: ts.source,
            medium: ts.medium,
            worker_country: workerCountry || "UNKNOWN",
            checkout_country_effective: effectiveCountry,
            product_surface:
              location.pathname === "/" ? "dual_cta_home" : "dual_cta_video_landing",
            landing_path: location.pathname,
            has_worker_dual_cta: Boolean(workerMux),
            // Solo true si país ganó la cadena (no ab_slot same-Mux).
            ...(slotPlaybackId
              ? {}
              : workerMux
                ? { mux_country_template_active: true }
                : {}),
            free_cta_enabled: freeCtaEnabled,
            has_free_telegram: freeCtaEnabled && Boolean(freeTelegramUrl),
            using_env_fallback:
              Boolean(brandMuxFallback) && effectiveId === brandMuxFallback,
            ...getVipPriceTelemetryFields(),
            ...abTrackingFields,
            ...getDualCtaGeoSlackFields(),
            ...getPostHogSlackSessionPropertiesPostHog(),
          },
        }),
      }).catch(() => {});
    } catch {
      /* never break UX */
    }

    // Meta Pixel — ViewContent: user landed on the video/membership page
    trackViewContentMeta({
      content_name: "DualCTA Video Landing",
      content_type: "page",
      content_category: "VIP Membership",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotIsReady, configIsReady, muxTeaserResolved, dualCtaCountryMux, pendingCountryTemplate, ctMux]);

  // ── Funnel: VIP CTA impression (rendered in DOM, once per visit) ───────────
  const vipImpressionFiredRef = useRef(false);
  useEffect(() => {
    if (!showVipCta || vipImpressionFiredRef.current) return;
    vipImpressionFiredRef.current = true;
    trackDualCta("dual_cta_vip_cta_impression", {
      embedded_social_browser: embeddedSocialBrowser,
      poster_ready: posterReady,
      video_playing: videoPlaying,
      ...getDualCtaCheckoutDiagnostics(),
      ...getPageContext(),
    });
  }, [
    showVipCta,
    embeddedSocialBrowser,
    posterReady,
    videoPlaying,
    getDualCtaCheckoutDiagnostics,
  ]);

  // ── Funnel: video still buffering — CTA should remain the primary action ───
  // CN (Mux CDN/GFW): surface the "join now" hint sooner — stream often never starts.
  const videoWaitingFiredRef = useRef(false);
  useEffect(() => {
    if (videoPlaying || videoEnded || videoBroken) {
      setVipCtaWaitingHint(false);
      return;
    }
    const isCn = String(effectiveCountry || "").toUpperCase() === "CN";
    const hintMs = isCn ? 1_500 : 2_500;
    const diagMs = isCn ? 2_000 : 3_000;
    const hintTimer = window.setTimeout(() => setVipCtaWaitingHint(true), hintMs);
    const diagTimer = window.setTimeout(() => {
      if (videoWaitingFiredRef.current || videoPlaying || videoEnded) return;
      videoWaitingFiredRef.current = true;
      trackDualCta("dual_cta_video_waiting_for_play", {
        seconds_since_mount: Math.round((Date.now() - mountTimeRef.current) / 1000),
        poster_ready: posterReady,
        video_ready: videoReady,
        embedded_social_browser: embeddedSocialBrowser,
        is_china_cdn_likely: isCn,
        conversion_possible: true,
        ...getDualCtaCheckoutDiagnostics(),
        ...getPageContext(),
      });
    }, diagMs);
    return () => {
      window.clearTimeout(hintTimer);
      window.clearTimeout(diagTimer);
    };
  }, [
    videoPlaying,
    videoEnded,
    videoBroken,
    posterReady,
    videoReady,
    embeddedSocialBrowser,
    effectiveCountry,
    getDualCtaCheckoutDiagnostics,
  ]);

  // ── Slack: visitor alerts on home video landing ──────────────────────────
  // Antes este path (la home `/` mundial) era el único embudo sin señal en
  // Slack — el operador no veía actividad hasta que el usuario navegaba a
  // /invitacion-premium. Ahora replicamos el patrón de BioTemplatePremiumSimple:
  //  1) registramos la visita en `premium_invite_views` para que el cron
  //     `slack-premium-invite-visits` la agregue cada 15 min (con país desde
  //     el header geo de Vercel/CF — ver server-side fallback).
  //  2) disparamos un alert real-time `page_enter_dual_cta_landing` una vez
  //     por sesión (sessionStorage flag + dedup 2 min server-side).
  // Reusamos la key `premium_invite_session_id` para que, si el visitante
  // navega a /invitacion-premium después, la sesión se correlacione en el
  // cron y no cuente como dos visitantes separados.
  // Session id + cron aggregator: fire once ASAP (no wait for A/B).
  const sessionBootFiredRef = useRef(false);
  useEffect(() => {
    if (sessionBootFiredRef.current) return;
    if (typeof window === "undefined") return;
    sessionBootFiredRef.current = true;

    let sessionId = "";
    try {
      sessionId = sessionStorage.getItem("premium_invite_session_id")?.trim() || "";
    } catch { /* storage blocked */ }
    if (!sessionId) {
      sessionId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      try { sessionStorage.setItem("premium_invite_session_id", sessionId); } catch { /* */ }
    }
    dualCtaSessionIdRef.current = sessionId;
    recordDualCtaSessionEnter(location.pathname);

    try {
      void fetch("/api/track-premium-invite-view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          path: window.location.pathname,
          language:
            (typeof navigator !== "undefined" && navigator.language) || "",
          sessionId,
          heroCta: "dual_cta",
          template: "DualCTAVideoLanding",
        }),
      }).catch(() => {});
    } catch { /* best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real-time page_enter Slack: wait until the same resolution gate as the
  // player / dual_cta_video_viewed so we never claim "country admin" when A/B
  // slot will win (same-Mux functional A/B was the classic lie).
  const slackEnterFiredRef = useRef(false);
  useEffect(() => {
    if (slackEnterFiredRef.current) return;
    if (typeof window === "undefined") return;
    if (pendingCountryTemplate) return;
    const resolutionReady =
      slotIsReady &&
      (Boolean(slotPlaybackId) ||
        Boolean(dualCtaCountryMux) ||
        Boolean(ctMux) ||
        (configIsReady && muxTeaserResolved));
    if (!resolutionReady) return;
    slackEnterFiredRef.current = true;

    const sessionId =
      dualCtaSessionIdRef.current ||
      (() => {
        try {
          return sessionStorage.getItem("premium_invite_session_id")?.trim() || "";
        } catch {
          return "";
        }
      })();
    if (!sessionId) return;

    const slackAlertKey = `dual_cta_slack_alerted:${sessionId}`;
    let alreadyAlerted = false;
    try { alreadyAlerted = Boolean(sessionStorage.getItem(slackAlertKey)); } catch { /* */ }
    if (alreadyAlerted) return;
    try { sessionStorage.setItem(slackAlertKey, "1"); } catch { /* */ }

    try {
      const ts = detectTrafficSource();
      const phCtx = getPostHogSlackSessionPropertiesPostHog();
      const funnelDiag = getSlackFunnelDiagnosticsExtraPostHog();
      const effectiveId = (
        slotPlaybackId ||
        dualCtaCountryMux ||
        ctMux ||
        playbackId ||
        muxTeaserId ||
        brandMuxFallback ||
        ENV_PLAYBACK_ID ||
        ""
      ).trim();
      void fetch("/api/track-funnel-failure", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          kind: "page_enter_dual_cta_landing",
          template: "DualCTAVideoLanding",
          country: "",
          flow_trace_id: sessionId,
          page_url: window.location.href,
          extra: {
            channel: ts.channel,
            source: ts.source,
            medium: ts.medium,
            in_app_browser: ts.inAppBrowser || undefined,
            ...(ts.raw.referrer_domain && { referrer: ts.raw.referrer_domain }),
            embedded_social_browser: embeddedSocialBrowser,
            has_playback_id_at_mount: Boolean(effectiveId),
            free_cta_enabled: freeCtaEnabled,
            has_free_telegram: freeCtaEnabled && Boolean(freeTelegramUrl),
            using_fallback_id: !slotPlaybackId && !dualCtaCountryMux && !ctMux && !playbackId,
            playback_id: effectiveId || undefined,
            playback_id_at_mount: effectiveId || undefined,
            playback_id_source: playbackIdSourceRef.current || undefined,
            playback_id_source_at_mount: playbackIdSourceRef.current || undefined,
            worker_template: "DualCTAVideoLanding",
            worker_mux_playback_id: (dualCtaCountryMux || ctMux || undefined),
            entry_path: location.pathname,
            entry_channel: ts.channel,
            vip_checkout_destination: vipCtaDestination,
            vip_checkout_gateway: vipCheckoutGateway,
            landing_path: location.pathname,
            worker_country: workerCountry || "UNKNOWN",
            checkout_country_effective: effectiveCountry,
            ...getVipPriceTelemetryFields(),
            ...abTrackingFields,
            product_surface: isPremiumInviteLandingPath(location.pathname)
              ? "premium_invite_landing"
              : location.pathname === "/"
                ? "dual_cta_home"
                : "dual_cta_video_landing",
            funnel: isPremiumInviteLandingPath(location.pathname)
              ? "comunidad_ar"
              : undefined,
            shop_region:
              (effectiveCountry || "").toUpperCase() === "AR" ? "AR" : "intl",
            ...getDualCtaGeoSlackFields(),
            ...phCtx,
            ...funnelDiag,
          },
        }),
      }).catch(() => {});
    } catch { /* never break UX */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slotIsReady,
    configIsReady,
    muxTeaserResolved,
    dualCtaCountryMux,
    pendingCountryTemplate,
    ctMux,
    slotPlaybackId,
  ]);

  /**
   * Prefer Mux playhead (max currentTime). Fall back to wall-clock since first play
   * only if telemetry never reported (very short plays / player without timeupdate).
   */
  const getVideoWatchedSeconds = (): number => {
    const playhead = maxPlayheadSecondsRef.current;
    if (playhead > 0.25) return Math.round(playhead);
    if (videoStartTimeRef.current !== null) {
      return Math.max(0, Math.round((Date.now() - videoStartTimeRef.current) / 1000));
    }
    return 0;
  };

  /** 0–100% of video duration reached (playhead / duration). Heuristic if duration unknown. */
  const getVideoMilestonePct = (watchedSec: number, ended: boolean): number => {
    if (ended) return 100;
    const dur = videoDurationSecondsRef.current;
    if (dur != null && dur > 1) {
      return Math.min(100, Math.max(0, Math.round((watchedSec / dur) * 100)));
    }
    // Teaser ~30–60 s heuristic (legacy) when duration not yet known
    if (watchedSec >= 30) return 75;
    if (watchedSec >= 15) return 50;
    if (watchedSec >= 5) return 25;
    return watchedSec > 0 ? 10 : 0;
  };

  /** Extra props for PostHog / Slack: watch + duration + measurement source. */
  const getVideoWatchTelemetryFields = (
    watchedSec: number,
    ended: boolean,
  ): Record<string, string | number | boolean> => {
    const playhead = maxPlayheadSecondsRef.current;
    const usedPlayhead = playhead > 0.25;
    const dur = videoDurationSecondsRef.current;
    return {
      video_watched_seconds: watchedSec,
      video_watch_source: usedPlayhead ? "mux_playhead" : "wall_clock",
      ...(dur != null && dur > 0 ? { video_duration_seconds: Math.round(dur) } : {}),
      video_milestone_pct: getVideoMilestonePct(watchedSec, ended),
      ...(playhead > 0
        ? { video_playhead_max_seconds: Math.round(playhead * 10) / 10 }
        : {}),
    };
  };

  // ── Funnel: no-action timeout — user saw the page but did nothing ──────────
  // Fires at 15 s if no CTA click and no video play. Diagnoses "page loaded but
  // nothing happened" cases (video stalled, price too high seen before video, etc.)
  useEffect(() => {
    noActionTimerRef.current = setTimeout(() => {
      if (ctaClickedRef.current) return; // already converted
      if (pageExitFiredRef.current) return; // ya registramos salida
      if (!viewedFiredRef.current) return; // página aún no lista (slot A/B)
      const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
      const env = getLandingEnvironmentFields();
      trackDualCta("dual_cta_no_action_timeout", {
        seconds_on_page: secOnPage,
        video_started: videoStartTimeRef.current !== null,
        video_ended: videoEnded,
        video_error_count: videoErrorCountRef.current,
        video_stall_count: stallCountRef.current,
        embedded_social_browser: embeddedSocialBrowser,
        has_free_telegram: freeCtaEnabled && Boolean(freeTelegramUrl),
        vip_cta_impression_fired: vipImpressionFiredRef.current,
        ...getDualCtaCheckoutDiagnostics(),
        ...getPageContext(),
      });
      // Soft-fail Slack: before this, only FATAL Mux onError hit Slack. Replays
      // 019f7a5f/75 had mediaheight=0 + HTTP without onError → silent. Escalate
      // idle + never-played to mux_playback_error so ops see it.
      if (videoStartTimeRef.current === null && !videoBrokenRef.current) {
        videoNeverPlayableSlackFiredRef.current = true;
        const isCn = String(effectiveCountry || "").toUpperCase() === "CN";
        const stuckKind = env.insecure_page
          ? "insecure_page_and_video_stuck"
          : isCn
            ? "mux_cdn_region_cn"
            : "video_stuck_not_playing";
        postSlackFunnelDualCta("mux_playback_error", dualCtaSessionIdRef.current, {
          hero_cta: "dual_cta",
          issue_kind: stuckKind,
          error_message: env.insecure_page
            ? `Video never played after ${secOnPage}s on ${env.page_protocol}:// (insecure_context). Soft-stall — Mux may not fire onError. NOT a conversion dropoff.`
            : isCn
              ? `CN soft-stall: video never played after ${secOnPage}s. Likely Mux CDN/GFW (stream.mux.com), not missing country config. CTAs still usable full-screen.`
              : `Video never played after ${secOnPage}s (no onPlaying). Soft-stall — no fatal Mux onError. NOT a conversion dropoff.`,
          seconds_since_mount: secOnPage,
          video_started: false,
          conversion_possible: true,
          not_a_conversion_dropoff: false,
          is_china_cdn_likely: isCn,
          embedded_social_browser: embeddedSocialBrowser,
          surface_dual_cta: true,
          ...env,
          ...getActivePlaybackFields(),
        });
      }
    }, 15_000);

    return () => {
      if (noActionTimerRef.current) clearTimeout(noActionTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Immediate Slack if landing is HTTP / non-secure (IG link-in-bio bug) ──
  // Worker now 301s to HTTPS, but if a client still lands on http (cache, old
  // edge, non-CF path) we MUST alert — previously only console (Clerk digest)
  // and soft Mux stall, zero Slack.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const env = getLandingEnvironmentFields();
    if (!env.insecure_page) return;
    const key = `dual_cta_insecure_slack:${dualCtaSessionIdRef.current || "x"}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* */ }
    trackDualCta("dual_cta_insecure_context", {
      ...env,
      embedded_social_browser: embeddedSocialBrowser,
      ...getPageContext(),
    });
    postSlackFunnelDualCta("mux_playback_error", dualCtaSessionIdRef.current, {
      hero_cta: "dual_cta",
      issue_kind: "insecure_page_context",
      error_message:
        `Page loaded over ${env.page_protocol}:// (isSecureContext=${env.is_secure_context}). ` +
        "Mux soft-stall + Clerk digest/origin fail in IG IAB. Expected https://www.",
      seconds_since_mount: 0,
      video_started: false,
      embedded_social_browser: embeddedSocialBrowser,
      surface_dual_cta: true,
      ...env,
      ...getActivePlaybackFields(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Funnel: page exit without conversion ──────────────────────────────────
  useEffect(() => {
    const handleExit = () => {
      if (pageExitFiredRef.current) return;

      // VIP click sin redirect a checkout → casi compra abandonada (Slack + journey).
      if (
        ctaClickedRef.current === "premium" &&
        !checkoutInitiatedRef.current &&
        !highIntentAbandonmentFiredRef.current
      ) {
        const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
        if (secOnPage >= 15) {
          highIntentAbandonmentFiredRef.current = true;
          const videoWatchedSec = getVideoWatchedSeconds();
          postDualCtaHighIntentAbandonment({
            sessionId: dualCtaSessionIdRef.current,
            pathname: location.pathname,
            country: effectiveCountry,
            secondsOnPage: secOnPage,
            videoWatchedSeconds: videoWatchedSec,
            extra: {
              ...getDualCtaCheckoutDiagnostics(),
              ...getVideoWatchTelemetryFields(videoWatchedSec, videoEnded),
              video_ended: videoEnded,
              video_played: videoPlaying,
            },
          });
        }
      }

      if (ctaClickedRef.current) return;
      // No marcar "salida" antes de que el embudo haya registrado la vista
      // (evita page_exit fantasma en tab background / slot A/B lento).
      if (!viewedFiredRef.current) return;
      pageExitFiredRef.current = true;
      if (noActionTimerRef.current) {
        clearTimeout(noActionTimerRef.current);
        noActionTimerRef.current = null;
      }
      const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
      // Mux playhead max (preferred) — real watch time, not wall-clock since play.
      const videoWatchedSec = getVideoWatchedSeconds();
      const watchFields = getVideoWatchTelemetryFields(videoWatchedSec, videoEnded);

      // Diagnose "why didn't they click?" — key signals:
      // - DualCTA VIP (+ free TG) CTAs are always usable without the teaser.
      //   conversion_possible tracks CTA availability, NOT whether video played.
      // - Never saw video → tech soft-stall (mux_playback_error), still convertible.
      // - Watched full video but no click → price concern / lost interest.
      // - Short time → immediate bounce.
      // - Video errors → technical failure; CTAs still full-screen when videoBroken.
      const videoStarted = videoStartTimeRef.current !== null;
      const neverSawVideo =
        !videoStarted && videoWatchedSec <= 0 && !videoEnded;
      const dualCtaCtasUsable =
        showVipCta || (freeCtaEnabled && Boolean(freeTelegramUrl));
      const exitReason =
        videoBrokenRef.current
          ? "video_error"
          : neverSawVideo && secOnPage >= 4
            ? "video_never_played"
            : videoEnded && secOnPage < 5
              ? "post_video_quick_exit"
              : videoEnded
                ? "watched_full_no_click"
                : videoWatchedSec > 10
                  ? "partial_watch_no_click"
                  : secOnPage < 4
                    ? "immediate_bounce"
                    : neverSawVideo
                      ? "video_never_played"
                      : "no_video_no_click";

      trackDualCta("dual_cta_page_exit", {
        exit_reason: exitReason,
        seconds_on_page: secOnPage,
        ...watchFields,
        video_started: videoStarted,
        video_ended: videoEnded,
        video_error_count: videoErrorCountRef.current,
        video_stall_count: stallCountRef.current,
        // Honest: DualCTA conversion does not require the teaser to play.
        conversion_possible: dualCtaCtasUsable,
        not_a_conversion_dropoff: false,
        dual_cta_ctas_usable: dualCtaCtasUsable,
        video_broken: videoBrokenRef.current,
        embedded_social_browser: embeddedSocialBrowser,
        has_free_telegram: freeCtaEnabled && Boolean(freeTelegramUrl),
        vip_cta_impression_fired: vipImpressionFiredRef.current,
        ...getDualCtaCheckoutDiagnostics(),
        ...getPageContext(),
      });

      if (exitReason === "video_error" && videoErrorCountRef.current > 0) {
        trackDualCtaMuxErrorNoConversion({
          trigger: "page_exit",
          pathname: location.pathname,
          country: effectiveCountry,
          errorCount: videoErrorCountRef.current,
          fatal: true,
          videoStarted,
          secondsSinceMount: secOnPage,
          playbackId: effectivePlaybackId,
          exitReason,
          extra: {
            ...watchFields,
            conversion_possible: dualCtaCtasUsable,
            not_a_conversion_dropoff: false,
            dual_cta_ctas_usable: dualCtaCtasUsable,
            embedded_social_browser: embeddedSocialBrowser,
          },
        });
      }

      // Slack rules for DualCTA exit:
      // 1) Video never played → tech alert (mux_playback_error). CTAs still usable.
      // 2) Soft-stall already fired → do not re-spam on exit.
      // 3) Real engagement (watched something, no click) → engagement_dropoff.
      // 4) video_error with CTAs shown long enough → engagement_dropoff too.
      // 5) immediate_bounce → skip generic dropoff.
      const tti =
        firstInteractionAtRef.current != null
          ? Math.round(
              (firstInteractionAtRef.current - mountTimeRef.current) / 1000,
            )
          : null;
      const dropoffBase = {
        hero_cta: "dual_cta",
        exit_reason: exitReason,
        seconds_on_page: secOnPage,
        ...watchFields,
        video_started: videoStarted,
        video_ended: videoEnded,
        video_error_count: videoErrorCountRef.current,
        video_stall_count: stallCountRef.current,
        scroll_attempted: scrollAttemptedRef.current,
        max_scroll_pct: maxScrollPctRef.current,
        tab_blur_count: tabBlurCountRef.current,
        ...(tti != null && { time_to_first_interaction_s: tti }),
        embedded_social_browser: embeddedSocialBrowser,
        has_free_telegram: freeCtaEnabled && Boolean(freeTelegramUrl),
        dual_cta_ctas_usable: dualCtaCtasUsable,
        surface_dual_cta: true,
        ...getActivePlaybackFields(),
      };

      if (
        (exitReason === "video_never_played" || neverSawVideo) &&
        exitReason !== "immediate_bounce"
      ) {
        if (!videoNeverPlayableSlackFiredRef.current) {
          videoNeverPlayableSlackFiredRef.current = true;
          const env = getLandingEnvironmentFields();
          const isCn = String(effectiveCountry || "").toUpperCase() === "CN";
          postSlackFunnelDualCta(
            "mux_playback_error",
            dualCtaSessionIdRef.current,
            {
              ...dropoffBase,
              issue_kind: env.insecure_page
                ? "insecure_page_and_video_stuck"
                : isCn
                  ? "mux_cdn_region_cn"
                  : "video_stuck_not_playing",
              error_message: isCn
                ? `CN: left without Mux play (${secOnPage}s). CDN/GFW likely — VIP/free CTAs still shown. Not admin misconfig.`
                : `Left without video ever playing (${secOnPage}s). ` +
                  "Teaser stuck — DualCTA CTAs were still usable (not a pure tech dead-end).",
              conversion_possible: dualCtaCtasUsable,
              not_a_conversion_dropoff: false,
              is_china_cdn_likely: isCn,
              ...env,
            },
          );
        }
      } else if (
        exitReason === "video_error" &&
        dualCtaCtasUsable &&
        secOnPage >= 8
      ) {
        postSlackFunnelDualCta("engagement_dropoff", dualCtaSessionIdRef.current, {
          ...dropoffBase,
          conversion_possible: true,
          not_a_conversion_dropoff: false,
          video_broken_with_usable_ctas: true,
        });
      } else if (
        exitReason !== "immediate_bounce" &&
        exitReason !== "video_error" &&
        exitReason !== "video_never_played"
      ) {
        postSlackFunnelDualCta("engagement_dropoff", dualCtaSessionIdRef.current, {
          ...dropoffBase,
          conversion_possible: dualCtaCtasUsable,
          not_a_conversion_dropoff: false,
        });
      }
    };

    // visibilitychange covers tab close, app switch on mobile, and most navigations
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        // Contamos el blur (tab switch). Si vuelve y vuelve a irse, contará
        // múltiples — señal real de multitasking / comparación de precios.
        tabBlurCountRef.current += 1;
        handleExit();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handleExit);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handleExit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoEnded, embeddedSocialBrowser, freeTelegramUrl]);

  // ── Funnel: scroll attempt — user tried to scroll (wants more info / confused) ──
  // También trackea TTI (primera interacción) y max scroll %.
  useEffect(() => {
    let fired = false;
    const markFirstInteraction = () => {
      if (firstInteractionAtRef.current == null) {
        firstInteractionAtRef.current = Date.now();
      }
    };
    const updateMaxScroll = () => {
      // body lock con position:fixed → scrollY del documento siempre es 0.
      // Mejor proxy: la cantidad acumulada que el usuario movió el dedo / rueda.
      // Aproximamos con scrollTop del scrollable container si existe; si no,
      // usamos window.scrollY como mejor esfuerzo (en mobile sin body lock
      // funciona). Es un best-effort, no número absoluto.
      const sy = window.scrollY || document.documentElement.scrollTop || 0;
      const sh = document.documentElement.scrollHeight - window.innerHeight;
      if (sh > 0) {
        const pct = Math.min(100, Math.max(0, Math.round((sy / sh) * 100)));
        if (pct > maxScrollPctRef.current) maxScrollPctRef.current = pct;
      }
    };
    const handleScroll = () => {
      scrollAttemptedRef.current = true;
      markFirstInteraction();
      updateMaxScroll();
      if (fired || ctaClickedRef.current) return;
      fired = true;
      const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
      trackDualCta("dual_cta_scroll_attempt", {
        seconds_on_page: secOnPage,
        video_started: videoStartTimeRef.current !== null,
        embedded_social_browser: embeddedSocialBrowser,
      });
      setCtaScrollHint(true);
      window.setTimeout(() => setCtaScrollHint(false), 3200);
    };
    const handlePointer = () => markFirstInteraction();
    const handleKey = () => markFirstInteraction();
    window.addEventListener("wheel", handleScroll, { passive: true });
    window.addEventListener("touchmove", handleScroll, { passive: true });
    window.addEventListener("scroll", updateMaxScroll, { passive: true });
    window.addEventListener("pointerdown", handlePointer, { passive: true });
    window.addEventListener("keydown", handleKey, { passive: true });
    return () => {
      window.removeEventListener("wheel", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
      window.removeEventListener("scroll", updateMaxScroll);
      window.removeEventListener("pointerdown", handlePointer);
      window.removeEventListener("keydown", handleKey);
    };
  }, [embeddedSocialBrowser]);

  // Cadena de resolución final (sin flash — el player monta solo cuando resolutionReady).
  // Precedencia (2026-07 multi-A/B; espejo resolveDualCtaPlaybackChain):
  //   1) slotPlaybackId — A/B ON con match de país (permite 2+ Mux en el mismo ISO2)
  //   2) dualCtaCountryMux — edge inject (country_template_config → KV → Worker)
  //   3) ctMux — vipVideoAssetId del país resuelto por API
  //   4) playbackId — premium-invite overlay
  //   5) muxTeaserId — Mux API teaser
  //   6) brandMuxFallback / CANONICAL
  // Para medir multi-video en MX: slot ON + 2 variantes con playback distintos
  // (countries=["MX"] opcional). dualCta país sigue como baseline si el slot no match.
  const baseResolvedPlaybackId =
    slotPlaybackId ||
    dualCtaCountryMux ||
    ctMux ||
    playbackId ||
    muxTeaserId ||
    brandMuxFallback ||
    CANONICAL_MUX_PLAYBACK_ID;

  // Experience tracking: solo atribuir variante A/B si el slot realmente ganó la cadena.
  const abTrackingFields = (() => {
    const base: Record<string, string | number | boolean> = {
      video_ab_slot_key: slotKey,
      video_ab_slot_enabled: Boolean(slotPlaybackId),
    };
    if (slotPlaybackId) {
      base.video_ab_variant_label = slotVariantLabel || `variant_${slotVariantIndex}`;
      base.video_ab_variant_index = slotVariantIndex;
      base.experience_variant_source = "ab_slot";
    } else if (dualCtaCountryMux || ctMux) {
      base.video_ab_variant_label = "baseline";
      base.video_ab_variant_index = -1;
      base.experience_variant_source = "country_template";
    } else if (playbackId) {
      // Comunidad /admin/comunidad → simple_mux_overlay_playback_id.
      base.video_ab_variant_label = "premium_invite_overlay";
      base.video_ab_variant_index = -1;
      base.experience_variant_source = "admin_premium_invite";
    } else {
      base.video_ab_variant_label = "baseline";
      base.video_ab_variant_index = -1;
      base.experience_variant_source = "env_baseline";
    }
    return base;
  })();

  // forceFallback: salta el ID roto → brand/edge/canonical (country-aware).
  // activeTemplate (vip_video_asset) gana cuando el A/B slot lo resolvió en server.
  const hardFallbackPlaybackId =
    brandMuxFallback || ENV_PLAYBACK_ID || CANONICAL_MUX_PLAYBACK_ID || "";
  const effectivePlaybackId =
    playbackIdOverride ||
    (activeTemplate?.mux_playback_id) ||
    (forceFallbackPlaybackId ? hardFallbackPlaybackId : null) ||
    baseResolvedPlaybackId;

  const hasAuthoritativeMux = Boolean(
    dualCtaCountryMux || ctMux || slotPlaybackId || playbackId || muxTeaserId,
  );
  // ENV / brandMux / canónico ya son IDs estables — no bloquear el player por
  // brand-runtime-config (prefetch async). Slot OFF o sin match cae acá sin
  // spinner infinito.
  const hasStablePlaybackFallback = Boolean(
    brandMuxFallback || ENV_PLAYBACK_ID || CANONICAL_MUX_PLAYBACK_ID,
  );

  // Gate de mount: SIEMPRE esperamos slot A/B (slotIsReady) en el init.
  // Aunque dualCta país traiga mux sync, las variantes reales (precio/label/peso/
  // cupón — a menudo con el MISMO playback_id) deben ganar en el primer paint.
  // Timeout del hook = 3s max.
  const resolutionReady =
    !pendingCountryTemplate &&
    slotIsReady &&
    (Boolean(slotPlaybackId) ||
      Boolean(dualCtaCountryMux) ||
      Boolean(ctMux) ||
      (configIsReady &&
        muxTeaserResolved &&
        (hasAuthoritativeMux || hasStablePlaybackFallback || brandRuntimeReady)));
  const playerMountGateOpen = resolutionReady;
  if (playerMountGateOpen && effectivePlaybackId) {
    playerMountedRef.current = true;
  }

  // ── Soft-stall → full-screen CTAs (Mux never fires onError, esp. CN/GFW) ───
  // After timeout: videoBroken layout (centred VIP + free TG), stop infinite spin.
  useEffect(() => {
    if (!playerMountGateOpen) return;
    if (videoPlaying || videoEnded || videoBroken) return;
    if (!effectivePlaybackId) return;
    const isCn = String(effectiveCountry || "").toUpperCase() === "CN";
    const stallMs = isCn ? 8_000 : 12_000;
    const t = window.setTimeout(() => {
      if (videoStartTimeRef.current != null) return;
      if (videoBrokenRef.current) return;
      if (ctaClickedRef.current) return;
      setVideoBroken(true);
      setVideoPlaying(false);
      setVipCtaWaitingHint(true);
      const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
      trackDualCta("dual_cta_video_soft_stall_cta_surface", {
        seconds_since_mount: secOnPage,
        stall_ms: stallMs,
        is_china_cdn_likely: isCn,
        conversion_possible: true,
        playback_id: effectivePlaybackId,
        embedded_social_browser: embeddedSocialBrowser,
        ...getDualCtaCheckoutDiagnostics(),
        ...getPageContext(),
      });
      if (!videoNeverPlayableSlackFiredRef.current) {
        videoNeverPlayableSlackFiredRef.current = true;
        const env = getLandingEnvironmentFields();
        postSlackFunnelDualCta("mux_playback_error", dualCtaSessionIdRef.current, {
          hero_cta: "dual_cta",
          issue_kind: env.insecure_page
            ? "insecure_page_and_video_stuck"
            : isCn
              ? "mux_cdn_region_cn"
              : "video_stuck_not_playing",
          error_message: isCn
            ? `CN soft-stall → full-screen CTAs after ${secOnPage}s (Mux CDN/GFW likely). Conversion still possible.`
            : `Soft-stall → full-screen CTAs after ${secOnPage}s (no onPlaying). Conversion still possible without teaser.`,
          seconds_since_mount: secOnPage,
          video_started: false,
          conversion_possible: true,
          not_a_conversion_dropoff: false,
          is_china_cdn_likely: isCn,
          dual_cta_cta_surfaced_early: true,
          embedded_social_browser: embeddedSocialBrowser,
          surface_dual_cta: true,
          ...env,
          ...getActivePlaybackFields(),
        });
      }
    }, stallMs);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    playerMountGateOpen,
    effectivePlaybackId,
    videoPlaying,
    videoEnded,
    videoBroken,
    effectiveCountry,
    embeddedSocialBrowser,
  ]);

  // Experience snapshot → every dual_cta_* event + PostHog super-props (admin hub).
  useEffect(() => {
    const variantSource = (abTrackingFields.experience_variant_source ||
      "unknown") as ExperienceVariantSource;
    const mux =
      (typeof effectivePlaybackId === "string" && effectivePlaybackId.trim()) ||
      dualCtaCountryMux ||
      ENV_PLAYBACK_ID ||
      CANONICAL_MUX_PLAYBACK_ID ||
      "";
    const { productSurface } = resolveDualCtaFlow(location.pathname);
    const isComunidadLanding = isPremiumInviteLandingPath(location.pathname);
    const snap = buildExperienceSnapshot({
      template: "DualCTAVideoLanding",
      component: "DualCTAVideoLanding",
      productSurface,
      workerCountry: workerCountry || undefined,
      effectiveCountry,
      variantSource,
      slotKey,
      slotEnabled: Boolean(abTrackingFields.video_ab_slot_enabled),
      variantLabel: String(abTrackingFields.video_ab_variant_label || "baseline"),
      variantIndex:
        typeof abTrackingFields.video_ab_variant_index === "number"
          ? abTrackingFields.video_ab_variant_index
          : -1,
      muxPlaybackId: mux,
      playbackIdSource: playbackIdSourceRef.current || undefined,
      vipStripePriceId: activeVipStripePriceId || undefined,
      vipPriceOfferLine: vipPriceOffer
        ? `${vipPriceOffer.full} → ${vipPriceOffer.firstMonth}`
        : undefined,
      vipDisplayFullUsd: activeFullPriceUsd ?? undefined,
      vipStripePriceSource: vipStripePriceSource || undefined,
      configVersion: slotConfigVersion || undefined,
      extra: {
        ...getVipPriceTelemetryFields(),
        ...(isComunidadLanding
          ? comunidadDualCtaExtraProps({
              country: effectiveCountry,
              vipCheckoutGateway,
              vipCheckoutOverrideActive: Boolean(premiumInviteVipBase?.trim()),
              freeCtaEnabled,
            })
          : {}),
      },
    });
    setDualCtaExperienceBase(snap);
    registerDualCtaExperienceContextPostHog(snap);
  }, [
    abTrackingFields,
    effectivePlaybackId,
    dualCtaCountryMux,
    workerCountry,
    effectiveCountry,
    slotKey,
    location.pathname,
    activeVipStripePriceId,
    vipPriceOffer,
    activeFullPriceUsd,
    vipStripePriceSource,
    getVipPriceTelemetryFields,
    slotConfigVersion,
    vipCheckoutGateway,
    premiumInviteVipBase,
    freeCtaEnabled,
  ]);

  // Al cambiar el playback resuelto, el poster debe volver a cargar antes de
  // retirar el spinner — evita flash del frame anterior.
  useEffect(() => {
    setPosterReady(false);
  }, [effectivePlaybackId, playerRemountKey]);

  /** Next Mux-tagged teaser not yet marked failed (env/admin ids included). */
  const pickNextPlaybackId = (failedId: string): string | null => {
    failedPlaybackIdsRef.current.add(failedId);
    const seen = new Set<string>();
    const pool: string[] = [];
    const altSource = embeddedSocialBrowser
      ? [
          ...playbackIdAlternativesRef.current.filter((x) => x !== brandMuxFallback),
          ...playbackIdAlternativesRef.current,
        ]
      : playbackIdAlternativesRef.current;
    for (const id of [...altSource, playbackId, brandMuxFallback]) {
      const trimmed = id?.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      pool.push(trimmed);
    }
    return pool.find((id) => !failedPlaybackIdsRef.current.has(id)) ?? null;
  };

  // Trackeo del SOURCE final del playback id efectivo. Decide en cada render
  // qué bucket atribuir al id resuelto (no qué setState se llamó). Orden =
  // cadena de resolución: slot A/B gana aunque el mux sea idéntico al de país
  // (same-Mux funcional). Mentir "admin/country" cuando ganó ab_slot rompe
  // ownership en Slack (edit links) y el análisis de variantes.
  if (slotPlaybackId && effectivePlaybackId === slotPlaybackId) {
    playbackIdSourceRef.current = "ab_slot";
  } else if (dualCtaCountryMux && effectivePlaybackId === dualCtaCountryMux) {
    playbackIdSourceRef.current = "country_template";
  } else if (ctMux && effectivePlaybackId === ctMux) {
    playbackIdSourceRef.current = "country_template";
  } else if (playbackIdOverride) {
    playbackIdSourceRef.current = playbackIdAlternativesRef.current.includes(
      playbackIdOverride,
    )
      ? "mux_api"
      : brandMuxFallback === playbackIdOverride
        ? "env"
        : "admin";
  } else if (forceFallbackPlaybackId) {
    playbackIdSourceRef.current = "env";
  } else if (muxTeaserId && effectivePlaybackId === muxTeaserId) {
    playbackIdSourceRef.current = "mux_api";
  } else if (playbackId && effectivePlaybackId === playbackId) {
    if (
      playbackIdSourceRef.current !== "admin" &&
      playbackIdSourceRef.current !== "country_template" &&
      playbackIdSourceRef.current !== "ab_slot"
    ) {
      playbackIdSourceRef.current =
        CACHED_CONFIG?.playbackId === playbackId
          ? "cache"
          : brandMuxFallback === playbackId
            ? "env"
            : "admin";
    }
  } else if (brandMuxFallback && effectivePlaybackId === brandMuxFallback) {
    playbackIdSourceRef.current = "env";
  }

  const handlePremiumCtaClick = useCallback(() => {
    ctaClickedRef.current = "premium";
    markDualCtaConverted();
    if (noActionTimerRef.current) clearTimeout(noActionTimerRef.current);
    const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
    const videoWatchedSec = getVideoWatchedSeconds();
    const ctaDestination = vipCtaDestination;
    trackDualCta("dual_cta_video_premium_click", {
      video_ended: videoEnded,
      video_played: videoPlaying,
      ...getVideoWatchTelemetryFields(videoWatchedSec, videoEnded),
      seconds_on_page: secOnPage,
      clicked_before_video_start: !videoPlaying && !videoEnded,
      clicked_during_video: videoPlaying && !videoEnded,
      clicked_after_video: videoEnded,
      video_error_count: videoErrorCountRef.current,
      embedded_social_browser: embeddedSocialBrowser,
      cta_destination: ctaDestination,
      cta_kind: "vip",
      dual_cta_session_id: dualCtaSessionIdRef.current,
      vip_checkout_gateway: vipCheckoutGateway,
      landing_path: location.pathname,
      flow_surface: isPremiumInviteLandingPath(location.pathname)
        ? "premium_invite_landing"
        : "dual_cta_home",
      detected_country: effectiveCountry,
      ...getActivePlaybackFields(),
      ...abTrackingFields,
      ...getPageContext(),
    });
    trackInitiateCheckoutMeta({
      content_name: "VIP Membership",
      content_type: "product",
      content_category: isPremiumInviteLandingPath(location.pathname)
        ? "Premium Invite Landing"
        : "DualCTA Video Landing",
    });
    postSlackFunnelDualCta("cta_click_vip", dualCtaSessionIdRef.current, {
      hero_cta: "dual_cta",
      cta_destination: ctaDestination,
      vip_checkout_gateway: vipCheckoutGateway,
      landing_path: location.pathname,
      detected_country: effectiveCountry,
      video_ended: videoEnded,
      video_played: videoPlaying,
      ...getVideoWatchTelemetryFields(videoWatchedSec, videoEnded),
      time_on_page_s: secOnPage,
      clicked_before_video_start: !videoPlaying && !videoEnded,
      clicked_during_video: videoPlaying && !videoEnded,
      clicked_after_video: videoEnded,
      video_error_count: videoErrorCountRef.current,
      embedded_social_browser: embeddedSocialBrowser,
      surface_dual_cta: true,
      ...getActivePlaybackFields(),
    });
    // getActivePlaybackFields is defined below but all its reactive deps
    // (effectivePlaybackId, vipCtaDestination, vipCheckoutGateway, location.pathname)
    // are already in this array via direct capture — the function itself is not reactive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoEnded,
    videoPlaying,
    embeddedSocialBrowser,
    vipCtaDestination,
    vipCheckoutGateway,
    location.pathname,
    effectiveCountry,
  ]);

  const handlePremiumCtaActivate = useCallback(
    (event: { preventDefault: () => void }) => {
      handlePremiumCtaClick();
      // Override admin por país: destino directo (pisa el checkout Stripe inline).
      // En la rama <a> el href ya es el override → navegación nativa; en la rama
      // <button> (stripe_subscription) navegamos manualmente.
      // MP href solo en AR — exterior no debe navegar a MercadoPago (GeoSim MX).
      if (vipHrefOverride) {
        const overrideIsMp =
          /mercadopago\.com|mpago\.la|preapproval_plan_id=/i.test(vipHrefOverride);
        const allowOverride =
          (effectiveCountry || "").toUpperCase() === "AR" || !overrideIsMp;
        if (allowOverride) {
          if (vipCheckoutGateway === "stripe_subscription") {
            event.preventDefault();
            window.location.assign(vipHrefOverride);
          }
          return;
        }
        // override MP en exterior → caer a Stripe abajo
      }
      if (vipCheckoutGateway === "stripe_subscription") {
        event.preventDefault();
        // Prefill best-effort: si clerk-js no cargó todavía (usuario anónimo,
        // el caso mayoritario), sigue sin email — Stripe lo pide en checkout.
        const clerkEmail =
          getClerkUserImperative()?.primaryEmailAddress?.emailAddress;
        const checkoutEmail = dualCtaEmail || getCheckoutEmailForDualCta(clerkEmail);
        // Embedded (flag PostHog): modal sobre el video, sin redirect. In-app
        // browsers (IG) quedan en el flujo hosted/form-nav de siempre.
        const preferEmbedded =
          !embeddedSocialBrowser && isEmbeddedCheckoutFlagOn();
        const vipCheckoutArgs: Parameters<typeof startVipMembershipVideoCheckout>[0] = {
          preferEmbedded,
          countryCode: effectiveCountry,
          pathname: location.pathname,
          languageForPlans: localeForPlansFromMuxLanguage(muxLang),
          email: checkoutEmail || undefined,
          // Exterior Stripe: canónico + promo 50% 1er mes.
          // AR / MercadoPago: sin descuento (precio full del plan MP).
          stripePriceId: activeVipStripePriceId,
          ...(isExteriorDualCta
            ? {
                stripePromotionCodeId: CANONICAL_VIP_FIRST_MONTH_PROMO_ID,
                discountRate: CANONICAL_VIP_FIRST_MONTH_DISCOUNT_PERCENT / 100,
                discountDurationMonths: 1,
              }
            : {}),
          template: "DualCTAVideoLanding",
          template_type: "DualCTAVideoLanding",
          ...(slotVariantIndex >= 0 ? { dual_variant_id: `slot_${slotVariantIndex}` } : {}),
          ...(activeVipVideoAssetId ? { vip_video_asset_id: activeVipVideoAssetId } : {}),
          onIntent: ({ gateway }) => {
            checkoutInitiatedRef.current = true;
            markDualCtaConverted();
            const { flowName, productSurface } = resolveDualCtaFlow(location.pathname);
            trackDualCta("dual_cta_checkout_initiated", {
              checkout_gateway: gateway,
              has_checkout_email: Boolean(checkoutEmail),
              cta_kind: "vip",
              dual_cta_session_id: dualCtaSessionIdRef.current,
              flow_name: flowName,
              product_surface: productSurface,
              detected_country: effectiveCountry,
              ...(activeVipVideoAssetId ? { vip_video_asset_id: activeVipVideoAssetId } : {}),
              stripe_price_id:
                activeVipStripePriceId ||
                String(getVipPriceTelemetryFields().vip_stripe_price_id || ""),
              first_month_discount_percent: activeFirstMonthDiscountPercent ?? 0,
              ...getVipPriceTelemetryFields(),
              ...getActivePlaybackFields(),
              ...getDualCtaCheckoutDiagnostics(),
              ...abTrackingFields,
              ...getPageContext(),
            });

            // Explicit event to identify which template was used in checkout (for PostHog breakdown + Slack reports)
            trackDualCta("dual_cta_template_used_in_checkout", {
              vip_video_asset_id: activeVipVideoAssetId,
              mux_playback_id: activePlaybackId,
              template_name: activeTemplate?.name,
              checkout_gateway: gateway,
              has_checkout_email: Boolean(checkoutEmail),
              source: BRAND_SOURCE_KEY,
              flow_name: flowName,
              product_surface: productSurface,
              detected_country: effectiveCountry,
            });
          },
        };
        lastVipCheckoutArgsRef.current = vipCheckoutArgs;
        void startVipMembershipVideoCheckout(vipCheckoutArgs).then((result) => {
          if (result.gateway === "stripe_embedded" && result.clientSecret) {
            setEmbeddedCheckoutSecret(result.clientSecret);
            trackDualCta("dual_cta_embedded_checkout_opened", {
              detected_country: effectiveCountry,
              has_checkout_email: Boolean(checkoutEmail),
              ...getPageContext(),
            });
          }
        });
      }
    },
    [
      handlePremiumCtaClick,
      vipHrefOverride,
      vipCheckoutGateway,
      effectiveCountry,
      isExteriorDualCta,
      location.pathname,
      muxLang,
      activeVipStripePriceId,
      activeFirstMonthDiscountPercent,
      getDualCtaCheckoutDiagnostics,
      getVipPriceTelemetryFields,
      abTrackingFields,
    ],
  );

  // Bundle de fields del video activo para todas las alertas Slack (page_enter,
  // cta_click, engagement_dropoff, mux_playback_error). Centralizado para que
  // todos los eventos lleven el mismo contrato y el operador pueda filtrar por
  // playback_id en Slack/PostHog sin abrir cada call site.
  const getActivePlaybackFields = (): Record<string, string | number | boolean> => {
    const alternatives = playbackIdAlternativesRef.current;
    const idx = alternatives.indexOf(effectivePlaybackId);
    // Contrato Slack/PostHog: fuente + variante + free CTA siempre presentes.
    // mux_country_template_active solo si la cadena ganadora es dualCta país
    // (no si ab_slot ganó con el mismo mux — ownership debe apuntar a A/B).
    const winnerIsCountryMux =
      !slotPlaybackId &&
      Boolean(dualCtaCountryMux || ctMux) &&
      (effectivePlaybackId === dualCtaCountryMux || effectivePlaybackId === ctMux);
    return {
      playback_id: effectivePlaybackId,
      playback_id_short: effectivePlaybackId.slice(-8),
      playback_id_source: playbackIdSourceRef.current,
      playback_id_alternatives_count: alternatives.length,
      ...(idx >= 0 && { playback_id_index: idx }),
      playback_id_is_env_fallback:
        Boolean(brandMuxFallback) && effectivePlaybackId === brandMuxFallback,
      ...(winnerIsCountryMux ? { mux_country_template_active: true } : {}),
      free_cta_enabled: freeCtaEnabled,
      has_free_telegram: freeCtaEnabled && Boolean(freeTelegramUrl),
      ...abTrackingFields,
      vip_checkout_destination: vipCtaDestination,
      vip_checkout_gateway: vipCheckoutGateway,
      landing_path: location.pathname,
      ...getVipPriceTelemetryFields(),
    };
  };

  // ── Free CTA: gate de registro Clerk antes de enviar a Telegram ─────────
  /** Ruta al portal free (+ email prefilled) para capturar lead y completar datos. */
  const buildFreeRegistroUrl = useCallback(
    (email?: string) => {
      const q = new URLSearchParams();
      q.set("plan_tier", "free");
      q.set("access_request", "1");
      q.set("from_dual_cta", "1");
      if (freeTelegramUrl?.trim()) {
        q.set("free_telegram_url", freeTelegramUrl.trim());
      }
      const em = (email || dualCtaEmail || "").trim().toLowerCase();
      if (em.includes("@")) q.set("email", em);
      return `${MEMBERSHIP_ROUTE.REGISTRO}?${q.toString()}`;
    },
    [freeTelegramUrl, dualCtaEmail],
  );

  const handleFreeJoin = useCallback(async (opts?: { fromRecoveryEmail?: boolean }) => {
    // Tracking idéntico al onClick original del <a> — se preserva en cualquier path
    ctaClickedRef.current = "free";
    markDualCtaConverted();
    if (noActionTimerRef.current) clearTimeout(noActionTimerRef.current);
    const secOnPage = Math.round((Date.now() - mountTimeRef.current) / 1000);
    const videoWatchedSec = getVideoWatchedSeconds();
    const watchFields = getVideoWatchTelemetryFields(videoWatchedSec, videoEnded);
    const email = dualCtaEmail.trim().toLowerCase();
    const hasEmail = email.includes("@");

    // Capturar email de recuperación antes de ir al portal (lead + recovery).
    if (hasEmail) {
      const { flowName } = resolveDualCtaFlow(location.pathname);
      const cc = resolveCheckoutCountryCode(workerCountry);
      persistDualCtaCheckoutEmail({
        email,
        country: cc,
        flowName,
        provider: cc === "AR" ? "mercadopago" : "stripe",
        amount: 0,
        currency: cc === "AR" ? "ARS" : "USD",
        planTier: "free",
      });
      setDualCtaEmailCaptured(true);
    }

    trackDualCta("dual_cta_video_free_click", {
      video_ended: videoEnded,
      video_played: videoPlaying,
      ...watchFields,
      seconds_on_page: secOnPage,
      clicked_before_video_start: !videoPlaying && !videoEnded,
      clicked_during_video: videoPlaying && !videoEnded,
      clicked_after_video: videoEnded,
      video_error_count: videoErrorCountRef.current,
      embedded_social_browser: embeddedSocialBrowser,
      cta_kind: "free",
      free_from_recovery_email: Boolean(opts?.fromRecoveryEmail),
      has_recovery_email: hasEmail,
      dual_cta_session_id: dualCtaSessionIdRef.current,
      ...getActivePlaybackFields(),
      ...getPageContext(),
      ...abTrackingFields,
    });
    trackLeadMeta({
      content_name: opts?.fromRecoveryEmail
        ? "Free join via recovery email"
        : "Free Telegram Channel",
      content_category: "DualCTA Video Landing",
    });
    postSlackFunnelDualCta(
      "cta_click_free_telegram",
      dualCtaSessionIdRef.current,
      {
        hero_cta: "dual_cta",
        telegram_outbound_host: (() => {
          try { return new URL(freeTelegramUrl ?? "").hostname; } catch { return ""; }
        })(),
        telegram_link_style: (freeTelegramUrl ?? "").includes("/+") ? "join" : "channel",
        video_ended: videoEnded,
        video_played: videoPlaying,
        ...watchFields,
        time_on_page_s: secOnPage,
        clicked_before_video_start: !videoPlaying && !videoEnded,
        clicked_during_video: videoPlaying && !videoEnded,
        clicked_after_video: videoEnded,
        video_error_count: videoErrorCountRef.current,
        embedded_social_browser: embeddedSocialBrowser,
        surface_dual_cta: true,
        free_cta_enabled: freeCtaEnabled,
        has_free_telegram: freeCtaEnabled && Boolean(freeTelegramUrl),
        free_from_recovery_email: Boolean(opts?.fromRecoveryEmail),
        has_recovery_email: hasEmail,
        ...getActivePlaybackFields(),
        ...abTrackingFields,
      },
    );

    // Gate de registro — lectura imperativa de Clerk (sin hooks: el provider
    // está diferido en la landing). clerk-js sin cargar ⇒ signed-out, que es
    // el default correcto para el visitante anónimo mayoritario.
    // Tradeoff aceptado (review 2026-07-16): en '/' clerk-js NO carga nunca
    // (provider diferido hasta navegar), así que un miembro ya logueado que
    // toca el CTA free igual va a /registro — la mayoría es anónima y
    // /registro con sesión Clerk activa redirige.
    const clerkUser = getClerkUserImperative();
    if (!clerkUser) {
      navigate(buildFreeRegistroUrl(email));
      return;
    }
    if (!isProfileComplete(clerkUser)) {
      const q = new URLSearchParams({ plan_tier: "free", access_request: "1" });
      if (freeTelegramUrl?.trim()) q.set("free_telegram_url", freeTelegramUrl.trim());
      if (hasEmail) q.set("email", email);
      navigate(`${MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO}?${q.toString()}`);
      return;
    }
    // Perfil completo — llamar a la API inline
    try {
      const token = await getClerkTokenImperative();
      if (token) {
        const resp = await fetch("/api/free-join", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ free_telegram_url: freeTelegramUrl }),
        });
        if (!resp.ok) {
          const payload = await resp.json().catch(() => ({})) as { error?: string };
          toast({
            title: "No pudimos registrar tu solicitud",
            description: payload.error ?? "Intentá de nuevo en unos minutos.",
            variant: "destructive",
          });
          return;
        }
      }
    } catch {
      // Network error — best-effort, navigate anyway (idempotent on retry)
    }
    navigate(`${MEMBERSHIP_ROUTE.PENDING_APPROVAL}?from_free=true`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    freeTelegramUrl,
    navigate,
    videoEnded,
    videoPlaying,
    embeddedSocialBrowser,
    slotPlaybackId,
    slotVariantLabel,
    slotVariantIndex,
    dualCtaEmail,
    buildFreeRegistroUrl,
    workerCountry,
    location.pathname,
  ]);

  const isValidRecoveryEmail = useCallback((raw: string) => {
    const email = raw.trim().toLowerCase();
    // Suficiente para lead capture; Clerk validará de nuevo en /registro.
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  }, []);

  /** Free join desde el bloque de email de recuperación (post-video). */
  const handleFreeJoinFromRecovery = useCallback(() => {
    if (freeJoinBusy) return;
    const email = dualCtaEmail.trim().toLowerCase();
    if (!isValidRecoveryEmail(email)) {
      setRecoveryEmailError(
        muxLang === "es"
          ? "Ingresá un email válido para continuar"
          : "Enter a valid email to continue",
      );
      // Feedback inmediato: foco al campo (sin toast que tape el CTA).
      requestAnimationFrame(() => {
        recoveryEmailInputRef.current?.focus();
        recoveryEmailInputRef.current?.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      });
      return;
    }
    setRecoveryEmailError(null);
    setDualCtaEmail(email);
    setFreeJoinBusy(true);
    void handleFreeJoin({ fromRecoveryEmail: true }).finally(() => {
      // Si la navegación falla o el user vuelve, re-habilitar el botón.
      setFreeJoinBusy(false);
    });
  }, [
    dualCtaEmail,
    freeJoinBusy,
    handleFreeJoin,
    isValidRecoveryEmail,
    muxLang,
  ]);

  // Auto-focus del email free solo si free CTA está ON (no robar foco del VIP).
  const recoveryEmailFocusedRef = useRef(false);
  useEffect(() => {
    if (!(videoEnded || videoBroken)) return;
    if (recoveryEmailFocusedRef.current) return;
    if (!freeCtaEnabled) return;
    // Si VIP es primario, no auto-focus al free (teclado móvil tapa el CTA pago).
    if (showVipCta && !dualCtaCfg?.freeCtaFirst) return;
    recoveryEmailFocusedRef.current = true;
    const t = window.setTimeout(() => {
      recoveryEmailInputRef.current?.focus({ preventScroll: true });
    }, 420);
    return () => window.clearTimeout(t);
  }, [videoEnded, videoBroken, freeCtaEnabled, showVipCta, dualCtaCfg?.freeCtaFirst]);

  return (
    <div
      data-ph-section="dual_cta_landing"
      data-ph-section-label="Dual CTA Video Landing"
      className={cn(
        "fixed inset-0 z-0 w-full h-dvh min-h-dvh overflow-hidden bg-black",
        embeddedSocialBrowser && "instagram-browser-modal instagram-browser-container",
      )}
      style={{
        // GPU compositing layer — critical for Instagram WebView position:fixed reliability
        WebkitTransform: "translate3d(0,0,0)",
        transform: "translate3d(0,0,0)",
        // Prevent iOS rubber-band scroll passthrough
        touchAction: "none",
        overscrollBehavior: "none",
        // Explicit max-height for variable-chrome WebViews
        maxHeight: "100dvh",
        // Layout/paint isolation: prevents external reflows from affecting the player
        contain: "layout paint style",
      }}
    >
      {/* ── Progressive blur-up backdrop (gradient → LQIP → full poster).
             LCP CRITICAL: paint poster ASAP with optimistic playback ID (env /
             Worker dualCta / canónico). Do NOT wait for A/B slot (≤3s) or Mux
             API — that black gap was the main driver of LCP p75 ~5.5s on Datadog.
             Player still waits for playerMountGateOpen so it never remounts. ── */}
      {(() => {
        const posterHidden = (videoPlaying && !videoBroken) || videoEnded;
        // Optimistic ID available on first paint; swap to final when gate opens.
        const posterMuxId = (
          dualCtaCfg?.posterPlaybackId ||
          (playerMountGateOpen && effectivePlaybackId
            ? effectivePlaybackId
            : null) ||
          dualCtaCountryMux ||
          ctMux ||
          ENV_PLAYBACK_ID ||
          brandMuxFallback ||
          CANONICAL_MUX_PLAYBACK_ID ||
          ""
        ).trim();
        if (!posterMuxId && !dualCtaCfg?.posterImageUrl) return null;
        // Prioridad del poster por país (/admin/country-template):
        // 1) posterImageUrl (imagen propia https) → pisa todo.
        // 2) posterPlaybackId / video resuelto (frame Mux).
        if (dualCtaCfg?.posterImageUrl) {
          return (
            <img
              key={`poster-img-${dualCtaCfg.posterImageUrl}`}
              src={dualCtaCfg.posterImageUrl}
              alt=""
              aria-hidden
              fetchPriority="high"
              decoding="async"
              className={cn(
                "absolute inset-0 z-[8] h-full w-full object-cover transition-opacity duration-300 ease-out",
                posterHidden ? "opacity-0" : "opacity-100",
              )}
              onLoad={() => setPosterReady(true)}
              onError={() => setPosterReady(true)}
            />
          );
        }
        return (
          <MuxPosterBackdrop
            key={`poster-${posterMuxId}`}
            className="z-[8]"
            playbackId={posterMuxId}
            // Frame del poster (posterTime) sobre el video que reproduce, o sobre
            // posterPlaybackId si el admin definió un video distinto para el poster.
            time={dualCtaCfg?.posterTime ?? 0}
            fitMode="smartcrop"
            hidden={posterHidden}
            onReady={() => setPosterReady(true)}
          />
        );
      })()}

      {/* ── Video player — deferred until slot resolves so effectivePlaybackId is
             final and the player never remounts due to A/B slot arriving late.
             Con mux por país (gana la cadena) monta de inmediato: el slot no
             puede pisarlo, esperarlo era espera muerta. ── */}
      {playerMountGateOpen && effectivePlaybackId && (
      <div className="absolute inset-0 z-[12] h-full w-full">
      <MuxVideoPlayerWithCTA
        key={`player-session-${playerRemountKey}`}
        playbackId={effectivePlaybackId}
        countryCode={effectiveCountry}
        forceManualPlaybackId={true}
        parentOwnsPlaybackRotation={true}
        fallbackPlaybackIds={muxPlaybackAlternatives}
        language={muxLang}
        displayMode="cover"
        containerClassName={
          embeddedSocialBrowser ? "instagram-browser-container h-full w-full" : "h-full w-full"
        }
        // Loop configurable por país (/admin/country-template); default false.
        loop={dualCtaCfg?.videoLoop === true}
        autoPlay="any"
        preferAutoplayWithSound={true}
        hideUnmutePrompt={false}
        fullViewportOnMobile={true}
        // Mux Data attribution (analytics only, no visible title)
        muxDataVideoTitle={DUAL_CTA_TEASER_TITLE}
        muxDataVideoSeries={DUAL_CTA_TEASER_EXTERNAL_ID}
        // No service/ctaUrl/ctaLabel → player shows video only, no bottom CTA button
        skipWorkerTemplateCta={true}
        externalCheckoutCta={true}
        // El poster + loading indicator los maneja DualCTAVideoLanding (arriba).
        // Suprime shimmer/heavy loader del player para evitar flash doble capa.
        hideLoadingShimmer={true}
        onReady={() => setVideoReady(true)}
        onPlay={() => setVideoReady(true)}
        onPlaybackTelemetry={(payload) => {
          // Mux currentTime is the source of truth for "how long they watched".
          if (
            Number.isFinite(payload.currentTimeSeconds) &&
            payload.currentTimeSeconds > maxPlayheadSecondsRef.current
          ) {
            maxPlayheadSecondsRef.current = payload.currentTimeSeconds;
          }
          if (
            payload.durationSeconds != null &&
            Number.isFinite(payload.durationSeconds) &&
            payload.durationSeconds > 0
          ) {
            videoDurationSecondsRef.current = payload.durationSeconds;
          }
          // On end, pin playhead to duration so watch == full video.
          if (payload.reason === "ended" && payload.durationSeconds != null) {
            maxPlayheadSecondsRef.current = Math.max(
              maxPlayheadSecondsRef.current,
              payload.durationSeconds,
            );
          }
        }}
        onPlaying={() => {
          if (!videoPlaying) {
            videoStartTimeRef.current = Date.now();
            trackDualCta("dual_cta_video_started", {
              using_fallback_id: !playbackId,
              seconds_to_play: Math.round((Date.now() - mountTimeRef.current) / 1000),
              embedded_social_browser: embeddedSocialBrowser,
              ...getDualCtaCheckoutDiagnostics(),
              ...abTrackingFields,
              ...getPageContext(),
            });
            trackViewContentMeta({
              content_name: "Teaser Video Play",
              content_category: "DualCTA Video Landing",
            });
          }
          setVideoPlaying(true);
          setVideoBroken(false);
        }}
        onEnded={() => {
          setVideoEnded(true);
          const watchedSec = getVideoWatchedSeconds();
          // If playhead never reported, approximate full watch from wall-clock.
          if (maxPlayheadSecondsRef.current < 0.25 && videoStartTimeRef.current) {
            const wall = Math.round((Date.now() - videoStartTimeRef.current) / 1000);
            maxPlayheadSecondsRef.current = Math.max(maxPlayheadSecondsRef.current, wall);
          }
          trackDualCta("dual_cta_video_ended", {
            ...getVideoWatchTelemetryFields(getVideoWatchedSeconds() || watchedSec, true),
            embedded_social_browser: embeddedSocialBrowser,
          });
          trackLeadMeta({
            content_name: "Teaser Video Completed",
            content_category: "DualCTA Video Landing",
          });
        }}
        onError={(message) => {
          const msgStr = String(message ?? "");
          const isInvalidPlaybackId =
            /URL or playback-id does not exist|may have used an Asset ID|Authorization error trying to access this video URL/i.test(
              msgStr,
            );
          const isDemuxerOrDecodeError =
            /demuxer|could_not_parse|decode|media_err_decode/i.test(msgStr);
          // Red/CDN caído o transitorio (el player ya agotó sus retries con
          // backoff antes de delegar acá — ver MuxVideoPlayerWithCTA).
          const isNetworkError =
            /network|connection|tunnel|manifestloaderror|media download|failed to fetch|timeout|loaderror|hlserror|fragloaderror/i.test(
              msgStr,
            );
          // Mainland China: stream.mux.com often blocked/unstable (GFW). Prefer
          // CDN classification over "revisá Playback ID" so ops don't chase admin.
          const isChinaVisitor =
            String(effectiveCountry || "").toUpperCase() === "CN";
          let muxIssueKind = "mux_fatal_onerror";
          if (isInvalidPlaybackId) {
            muxIssueKind = "mux_invalid_playback_id";
          } else if (isChinaVisitor) {
            // CN + non-invalid: treat as regional CDN/network (not missing admin config)
            muxIssueKind = "mux_cdn_region_cn";
          } else if (isNetworkError) {
            muxIssueKind = "mux_fatal_network";
          } else if (isDemuxerOrDecodeError) {
            muxIssueKind = "mux_fatal_decode";
          }
          const nextPlaybackId = pickNextPlaybackId(effectivePlaybackId);
          const willRotateToAlternate = Boolean(nextPlaybackId);
          const isOnHardcodedFallback =
            Boolean(brandMuxFallback) && effectivePlaybackId === brandMuxFallback;
          // 2026-07-16: el swap a brand fallback aplicaba SOLO a playback IDs
          // inválidos — un error de red con la rotación agotada iba directo a
          // videoBroken sin intentar el fallback (PostHog: "Video falló —
          // abandonó por bug"). Red y decode ahora también swapean.
          const willSwapToBrandFallback =
            (isInvalidPlaybackId || isNetworkError || isDemuxerOrDecodeError) &&
            !willRotateToAlternate &&
            Boolean(hardFallbackPlaybackId.trim()) &&
            hardFallbackPlaybackId !== effectivePlaybackId &&
            !failedPlaybackIdsRef.current.has(hardFallbackPlaybackId) &&
            !isOnHardcodedFallback &&
            !forceFallbackPlaybackId;
          const willRetryChainForNetwork =
            isNetworkError &&
            !willRotateToAlternate &&
            !willSwapToBrandFallback &&
            networkChainRetryCountRef.current < 1;
          const isRecoverableError =
            willRotateToAlternate || willSwapToBrandFallback;
          trackDualCta("dual_cta_video_error", {
            message,
            error_count: videoErrorCountRef.current + 1,
            fatal: !isRecoverableError,
            seconds_since_mount: Math.round((Date.now() - mountTimeRef.current) / 1000),
            video_started: videoStartTimeRef.current !== null,
            embedded_social_browser: embeddedSocialBrowser,
            playback_id: effectivePlaybackId,
            using_fallback_id: forceFallbackPlaybackId || (!muxTeaserId && !playbackId),
            is_demuxer_error: isDemuxerOrDecodeError,
            is_network_error: isNetworkError,
            will_rotate_playback_id: willRotateToAlternate,
            next_playback_id: nextPlaybackId?.slice(-8),
            will_swap_to_fallback: willSwapToBrandFallback,
            will_retry_chain: willRetryChainForNetwork,
          });
          if (willRotateToAlternate && nextPlaybackId) {
            clearCachedPlaybackId();
            setVideoBroken(false);
            setVideoPlaying(false);
            setVideoReady(false);
            setVideoEnded(false);
            setPlaybackIdOverride(nextPlaybackId);
            setPlayerRemountKey((k) => k + 1);
            return;
          }
          if (willSwapToBrandFallback) {
            clearCachedPlaybackId();
            // Mismos resets que la rotación: sin ellos el player remonta con
            // videoReady/videoPlaying stale y el overlay de loading no vuelve.
            setVideoBroken(false);
            setVideoPlaying(false);
            setVideoReady(false);
            setVideoEnded(false);
            setPlaybackIdOverride(null);
            setForceFallbackPlaybackId(true);
            setPlayerRemountKey((k) => k + 1);
            return;
          }
          // Fatal from the player (retries/fallback exhausted). Count only here
          // so engagement_dropoff no marca "video_error" por errores recuperables.
          videoErrorCountRef.current += 1;
          setVideoBroken(true);
          setVideoPlaying(false);
          // Último recurso solo-red: un retry diferido de la cadena completa.
          // Mientras tanto videoBroken deja el CTA full-screen (la conversión
          // no depende del video). Si la red volvió (wifi→LTE, CDN blip), el
          // teaser reaparece; si falla de nuevo, el contador bloquea más loops.
          if (willRetryChainForNetwork) {
            networkChainRetryCountRef.current += 1;
            networkChainRetryTimerRef.current = window.setTimeout(() => {
              networkChainRetryTimerRef.current = null;
              failedPlaybackIdsRef.current.clear();
              setPlaybackIdOverride(null);
              setForceFallbackPlaybackId(false);
              setVideoBroken(false);
              setVideoPlaying(false);
              setVideoReady(false);
              setVideoEnded(false);
              setPlayerRemountKey((k) => k + 1);
            }, 8_000);
          }
          // Slack — alert real-time del Mux error (mismo síntoma que el incidente
          // 2026-05 con playback IDs apuntando a otra cuenta). Dedup default
          // 5 min por (kind+template+user) ya lo hace `sendFunnelAlert`.
          if (videoErrorCountRef.current <= 3) {
            postSlackFunnelDualCta(
              "mux_playback_error",
              dualCtaSessionIdRef.current,
              {
                hero_cta: "dual_cta",
                issue_kind: muxIssueKind,
                error_message: String(message ?? "").slice(0, 500),
                error_count: videoErrorCountRef.current,
                seconds_since_mount: Math.round((Date.now() - mountTimeRef.current) / 1000),
                video_started: videoStartTimeRef.current !== null,
                embedded_social_browser: embeddedSocialBrowser,
                using_fallback_id: forceFallbackPlaybackId || (!muxTeaserId && !playbackId),
                will_swap_to_fallback: willSwapToBrandFallback,
                is_network_error: isNetworkError,
                is_demuxer_error: isDemuxerOrDecodeError,
                is_invalid_playback_id: isInvalidPlaybackId,
                is_china_cdn_likely: muxIssueKind === "mux_cdn_region_cn",
                conversion_possible: true, // full-screen VIP (+ free TG) when videoBroken
                not_a_conversion_dropoff: false,
                dual_cta_ctas_usable: true,
                surface_dual_cta: true,
                ...getLandingEnvironmentFields(),
                ...getActivePlaybackFields(),
              },
            );
          }
          trackDualCtaMuxErrorNoConversion({
            trigger: "fatal_playback",
            pathname: location.pathname,
            country: effectiveCountry,
            errorCount: videoErrorCountRef.current,
            message: msgStr,
            fatal: true,
            videoStarted: videoStartTimeRef.current !== null,
            secondsSinceMount: Math.round((Date.now() - mountTimeRef.current) / 1000),
            playbackId: effectivePlaybackId,
            extra: {
              issue_kind: muxIssueKind,
              is_demuxer_error: isDemuxerOrDecodeError,
              is_network_error: isNetworkError,
              is_invalid_playback_id: isInvalidPlaybackId,
              is_china_cdn_likely: muxIssueKind === "mux_cdn_region_cn",
              embedded_social_browser: embeddedSocialBrowser,
              using_fallback_id: forceFallbackPlaybackId || (!muxTeaserId && !playbackId),
              conversion_possible: true,
              dual_cta_ctas_usable: true,
            },
          });
        }}
        analyticsFlowSurface={DUAL_CTA_MUX_ANALYTICS_SURFACE}
      />
      </div>
      )}

      {/* ── Loading estable (sin pulse en el CTA de conversión).
             1) Resolviendo ID (slot + Mux): fullscreen loading — CTAs aún no.
             2) Player montado, buffer: pill quieta sobre el poster; CTA sólido abajo. ── */}
      {!videoPlaying && !videoEnded && !videoBroken && (
        <div
          className="pointer-events-none absolute inset-0 z-[20]"
          role="status"
          aria-live="polite"
          aria-busy={!playerMountGateOpen || !videoReady}
        >
          {!playerMountGateOpen ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/50">
              <div className="relative h-10 w-10" aria-hidden>
                <div className="absolute inset-0 rounded-full border-2 border-white/15" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400 animate-spin" />
              </div>
              <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
                {label("loading", L.loading)}
              </span>
            </div>
          ) : (
            <div
              className="absolute inset-x-0 bottom-0 flex justify-center"
              style={{
                paddingBottom:
                  "max(6.75rem, calc(env(safe-area-inset-bottom, 0px) + 6.75rem))",
              }}
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-black/45 px-3 py-1.5 backdrop-blur-md ring-1 ring-white/10">
                <span className="relative h-3 w-3 shrink-0" aria-hidden>
                  <span className="absolute inset-0 rounded-full border-2 border-white/15" />
                  <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400 animate-spin" />
                </span>
                <span className="text-[10px] font-medium tracking-wide text-white/75">
                  {label("loading", L.loading)}
                </span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Broken video: poster visible, hide frozen demuxer frame ── */}
      {videoBroken && (
        <>
          <div
            className="pointer-events-none absolute inset-0 z-[101] bg-black/55"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 z-[102] flex items-center justify-center">
            <p className="rounded-xl bg-black/60 px-5 py-3 text-center text-sm text-white/70">
              {dualCtaCfg?.videoUnavailableText ||
                (
                  {
                    en: "Video unavailable",
                    es: "Video no disponible",
                    de: "Video nicht verfügbar",
                    fr: "Vidéo non disponible",
                    it: "Video non disponibile",
                    pt: "Vídeo indisponível",
                    nl: "Video niet beschikbaar",
                    tr: "Video kullanılamıyor",
                    ar: "الفيديو غير متاح",
                    pl: "Wideo niedostępne",
                    ja: "動画は利用できません",
                    ko: "동영상을 사용할 수 없습니다",
                    id: "Video tidak tersedia",
                  } as Record<string, string>
                )[muxLang] ||
                "Video unavailable"}
            </p>
          </div>
        </>
      )}

      {/* ── End-state black cover (video finished → focus attention on CTAs) ── */}
      {videoEnded && (
        <div
          className="absolute inset-0 z-[100] bg-black/90 transition-opacity duration-500"
          aria-hidden
        />
      )}

      {/* ── CTA overlay ────────────────────────────────────────────────────
          During playback: only the bottom strip is in the hit-tree so the
          center unmute affordance (player z-12) stays tappable. Full-screen
          only after the teaser ends (focus on CTAs).
          CTAs solo cuando resolutionReady — evita montar/desmontar/parpadeo
          mientras el slot A/B o Mux API aún resuelven el playback. */}
      <div
        className={cn(
          "absolute inset-x-0 flex flex-col pointer-events-none",
          videoEnded || videoBroken ? "inset-y-0" : "bottom-0",
        )}
        style={{ zIndex: 110 }}
      >
        {/* Bottom gradient scrim for legibility during playback */}
        {!videoEnded && playerMountGateOpen && (
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 [@media(min-height:700px)]:h-52"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.20) 60%, transparent 100%)",
            }}
            aria-hidden
          />
        )}

        {/* CTA region — bottom during playback, centred when video ends.
            Hidden until player mount gate: loading owns the first paint. */}
        {playerMountGateOpen && (
        <div
          className={cn(
            "relative flex justify-center px-5",
            videoEnded || videoBroken
              ? "flex-1 items-center"
              : "items-end pt-4",
          )}
          style={
            videoEnded || videoBroken
              ? undefined
              : {
                  paddingBottom:
                    "max(2rem, env(safe-area-inset-bottom, 2rem))",
                }
          }
        >
          <div
            className={cn(
              "pointer-events-auto flex w-full max-w-sm flex-col items-stretch",
              embeddedSocialBrowser && "instagram-browser-buttons",
              videoEnded ? "gap-6" : "gap-5",
              // Scroll / waiting hint: anillo estático breve — sin animate-pulse (titila).
              (ctaScrollHint || vipCtaWaitingHint) &&
                "rounded-3xl ring-1 ring-amber-300/50 ring-offset-1 ring-offset-black/30",
            )}
          >
            {vipCtaWaitingHint && showVipCta && !videoPlaying && !videoEnded && (
              <p className="text-center text-[12px] font-medium leading-snug text-white/85">
                {dualCtaCfg?.loadingJoinHint ||
                  (muxLang === "en"
                    ? "Video is loading — you can join now"
                    : "El video está cargando — podés unirte ahora")}
              </p>
            )}
            {checkoutReturnError && (
              <p
                className="rounded-xl bg-red-950/80 px-4 py-3 text-center text-[13px] leading-snug text-red-200 ring-1 ring-red-500/30"
                role="alert"
              >
                {checkoutReturnError}
              </p>
            )}

            {vipPriceOffer && showVipCta && !isArVipMercadoPago && (
              <div className="text-center">
                <p className="text-[12px] leading-snug text-white/80">
                  <span className="line-through opacity-55 mr-1.5">{vipPriceOffer.full}</span>
                  <span className="font-semibold text-amber-200">{vipPriceOffer.firstMonth}</span>
                  <span className="text-white/55">
                    {muxLang === "es" ? " primer mes" : " first month"}
                  </span>
                </p>
                {showStripeTrustBadge && (
                  <div className="mt-0.5">
                    <img
                      src={BRAND_POWERED_BY_STRIPE_URL}
                      alt="Pago seguro procesado por Stripe"
                      className="mx-auto h-2.5 opacity-70 md:h-3"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            )}
            {/* AR: precio full + logo Mercado Pago (sin promo 50% — solo Stripe exterior) */}
            {showVipCta && isArVipMercadoPago && (
              <div className="flex flex-col items-center gap-1.5 text-center">
                {vipPriceOffer && (
                  <p className="text-[12px] font-semibold tabular-nums text-amber-200/95">
                    {vipPriceOffer.full}
                    <span className="ml-1 font-normal text-white/55">
                      {muxLang === "es" ? "/mes" : "/mo"}
                    </span>
                  </p>
                )}
                <div
                  className="rounded-lg bg-white px-3 py-1.5 shadow-md shadow-black/30"
                  aria-label="Pago seguro con Mercado Pago"
                >
                  <img
                    src={BRAND_MERCADOPAGO_HANDSHAKE_URL}
                    alt="Mercado Pago"
                    className="mx-auto h-6 w-auto max-w-[min(100%,180px)] object-contain md:h-7"
                    loading="eager"
                    draggable={false}
                  />
                </div>
              </div>
            )}

            {/* Primary VIP + trust logo — SIEMPRE primero salvo freeCtaFirst.
                Soft-stall / video end: VIP manda la conversión; free es secundario. */}
            {showVipCta && (
              <div
                className="flex w-full flex-col items-stretch gap-1.5"
                style={{
                  // Orden por país: gratis-first → VIP+trust va segundo.
                  order: dualCtaCfg?.freeCtaFirst ? 2 : 1,
                }}
              >
                {vipCheckoutGateway === "stripe_subscription" && !vipHrefOverride ? (
                  <button
                    type="button"
                    onClick={handlePremiumCtaActivate}
                    className={cn(
                      "group flex w-full items-center justify-center gap-2 rounded-full",
                      "bg-amber-400/95 hover:bg-amber-300 active:scale-[0.97] active:bg-amber-500",
                      "text-black text-sm font-semibold tracking-tight",
                      "px-5 py-3.5",
                      "shadow-[0_2px_14px_rgba(251,191,36,0.28)] hover:shadow-[0_2px_18px_rgba(251,191,36,0.38)]",
                      "ring-1 ring-amber-300/40",
                      "transition-[transform,background-color,box-shadow] duration-150 ease-out",
                      "border-0 cursor-pointer",
                    )}
                    style={{
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                      minHeight: 48,
                    }}
                  >
                    <span className="truncate">{label("vipLabel", L.joinVip)}</span>
                    <span
                      className="text-[11px] opacity-70 transition-transform duration-150 group-hover:translate-x-0.5"
                      aria-hidden
                    >
                      →
                    </span>
                  </button>
                ) : (
                  <a
                    href={primaryCheckoutHref}
                    onClick={handlePremiumCtaActivate}
                    className={cn(
                      "group flex w-full items-center justify-center gap-2 rounded-full",
                      "bg-amber-400/95 hover:bg-amber-300 active:scale-[0.97] active:bg-amber-500",
                      "text-black text-sm font-semibold tracking-tight",
                      "px-5 py-3.5",
                      "shadow-[0_2px_14px_rgba(251,191,36,0.28)] hover:shadow-[0_2px_18px_rgba(251,191,36,0.38)]",
                      "ring-1 ring-amber-300/40",
                      "transition-[transform,background-color,box-shadow] duration-150 ease-out",
                      "no-underline",
                    )}
                    style={{
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                      minHeight: 48,
                    }}
                  >
                    <span className="truncate">{label("vipLabel", L.joinVip)}</span>
                    <span
                      className="text-[11px] opacity-70 transition-transform duration-150 group-hover:translate-x-0.5"
                      aria-hidden
                    >
                      →
                    </span>
                  </a>
                )}

                {showStripeTrustBadge && (
                  <div className="flex justify-center" aria-label="Pago seguro con Stripe">
                    <img
                      src={BRAND_POWERED_BY_STRIPE_URL}
                      alt="Pago seguro procesado por Stripe"
                      className="h-3.5 opacity-80 md:h-4"
                      loading="lazy"
                    />
                  </div>
                )}
                {/* Logo MP debajo del botón VIP (AR) — visible y con fondo blanco */}
                {showMercadoPagoTrustBadge && (
                  <div
                    className="flex justify-center rounded-xl bg-white px-4 py-2 shadow-md shadow-black/25"
                    aria-label="Pago seguro con Mercado Pago"
                    data-ph-capture-attribute-mp-logo="dual_cta_vip"
                  >
                    <img
                      src={BRAND_MERCADOPAGO_HANDSHAKE_URL}
                      alt="Mercado Pago — pago seguro"
                      className="h-7 w-auto max-w-[min(100%,200px)] object-contain md:h-8"
                      loading="eager"
                      draggable={false}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Free join post-video: SOLO si free CTA está ON (admin dual_cta_free_enabled).
                Antes se mostraba también con showVipCta solo → "Entrar gratis" encima del VIP
                aunque el free estuviera apagado (soft-stall / GeoSim). */}
            {freeCtaEnabled && (videoEnded || videoBroken) && (
              <div
                className="space-y-2.5 rounded-2xl border border-white/12 bg-white/[0.06] px-3 py-3"
                data-ph-section="dual_cta_recovery_free_join"
                style={{
                  order: dualCtaCfg?.freeCtaFirst ? 1 : 2,
                }}
              >
                <p className="text-center text-[12px] font-medium leading-snug text-white/80">
                  {muxLang === "es"
                    ? "Dejá tu email y entrá gratis a completar tu acceso"
                    : "Leave your email and join free to complete access"}
                </p>
                <div className="space-y-1.5">
                  <label htmlFor="dual-cta-recovery-email" className="sr-only">
                    Email
                  </label>
                  <input
                    ref={recoveryEmailInputRef}
                    id="dual-cta-recovery-email"
                    type="email"
                    name="email"
                    inputMode="email"
                    autoComplete="email"
                    enterKeyHint="go"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={dualCtaEmail}
                    disabled={freeJoinBusy}
                    onChange={(e) => {
                      setDualCtaEmail(e.target.value);
                      setDualCtaEmailCaptured(false);
                      if (recoveryEmailError) setRecoveryEmailError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleFreeJoinFromRecovery();
                      }
                    }}
                    onBlur={() => {
                      const email = dualCtaEmail.trim().toLowerCase();
                      if (email && isValidRecoveryEmail(email)) {
                        setDualCtaEmail(email);
                        const { flowName } = resolveDualCtaFlow(location.pathname);
                        const cc = resolveCheckoutCountryCode(workerCountry);
                        persistDualCtaCheckoutEmail({
                          email,
                          country: cc,
                          flowName,
                          provider: cc === "AR" ? "mercadopago" : "stripe",
                          amount: 0,
                          currency: cc === "AR" ? "ARS" : "USD",
                          planTier: "free",
                        });
                      }
                    }}
                    placeholder={
                      muxLang === "es" ? "tu@email.com" : "you@email.com"
                    }
                    aria-invalid={Boolean(recoveryEmailError)}
                    aria-describedby={
                      recoveryEmailError
                        ? "dual-cta-recovery-email-error"
                        : "dual-cta-recovery-email-hint"
                    }
                    className={cn(
                      "w-full rounded-full bg-black/35 px-4 py-3 text-sm text-white",
                      "placeholder:text-white/40",
                      "border transition-[border-color,box-shadow] duration-150 ease-out",
                      "focus:outline-none focus:ring-2 focus:ring-offset-0",
                      "disabled:opacity-60",
                      recoveryEmailError
                        ? "border-rose-400/70 focus:border-rose-300 focus:ring-rose-400/30"
                        : "border-white/20 focus:border-emerald-300/70 focus:ring-emerald-400/25",
                    )}
                    style={{ minHeight: 48 }}
                  />
                  {recoveryEmailError ? (
                    <p
                      id="dual-cta-recovery-email-error"
                      role="alert"
                      className="px-1 text-center text-[11px] font-medium leading-snug text-rose-200"
                    >
                      {recoveryEmailError}
                    </p>
                  ) : (
                    <p
                      id="dual-cta-recovery-email-hint"
                      className="px-1 text-center text-[10px] leading-snug text-white/45"
                    >
                      {muxLang === "es"
                        ? "Gratis · sin tarjeta · completás tu perfil en el siguiente paso"
                        : "Free · no card · complete your profile next"}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleFreeJoinFromRecovery}
                  disabled={freeJoinBusy}
                  className={cn(
                    "group flex w-full items-center justify-center gap-2 rounded-full",
                    "bg-white text-black text-sm font-semibold tracking-tight",
                    "px-5 py-3.5",
                    "shadow-[0_2px_16px_rgba(255,255,255,0.12)]",
                    "ring-1 ring-white/30",
                    "transition-[transform,opacity,background-color] duration-150 ease-out",
                    "hover:bg-white/95 active:scale-[0.97]",
                    "disabled:pointer-events-none disabled:opacity-70",
                  )}
                  style={{
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent",
                    minHeight: 48,
                  }}
                >
                  {freeJoinBusy ? (
                    <>
                      <span
                        className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-black/20 border-t-black"
                        aria-hidden
                      />
                      <span className="truncate">
                        {muxLang === "es" ? "Continuando…" : "Continuing…"}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="truncate">
                        {muxLang === "es" ? "Entrar gratis" : "Join free"}
                      </span>
                      <span
                        className="text-[11px] opacity-60 transition-transform duration-150 group-hover:translate-x-0.5"
                        aria-hidden
                      >
                        →
                      </span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Secondary CTA — free Telegram (glass pill, only when URL is set AND admin toggle on).
                En post-video el free join ya está en el bloque de email (arriba). */}
            {freeCtaEnabled && freeTelegramUrl && !(videoEnded || videoBroken) && (
              <button
                type="button"
                onClick={() => { void handleFreeJoin(); }}
                className={cn(
                  "flex w-full items-center justify-center gap-2 rounded-full",
                  "bg-white/10 hover:bg-white/15 active:bg-white/20",
                  "backdrop-blur-md",
                  "text-white text-sm font-medium tracking-tight",
                  "px-4 py-3",
                  "ring-1 ring-white/20 hover:ring-white/30",
                  "ui-transition duration-150",
                )}
                style={{
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  minHeight: 44,
                  // Orden por país: gratis-first → el CTA gratis va primero.
                  order: dualCtaCfg?.freeCtaFirst ? 1 : 2,
                }}
              >
                <span className="truncate">{label("freeLabel", L.freePlanCta)}</span>
              </button>
            )}
          </div>
        </div>
        )}
      </div>

      {/* ── Embedded Checkout (flag PostHog) — modal sobre el video ── */}
      {embeddedCheckoutSecret && (
        <Suspense fallback={null}>
          <StripeEmbeddedCheckoutModal
            clientSecret={embeddedCheckoutSecret}
            onClose={() => {
              setEmbeddedCheckoutSecret(null);
              trackDualCta("dual_cta_embedded_checkout_closed", {
                ...getPageContext(),
              });
            }}
            onError={(message) => {
              // Mount falló (Stripe.js bloqueado, red, etc.) → hosted de siempre.
              setEmbeddedCheckoutSecret(null);
              trackDualCta("dual_cta_embedded_checkout_fallback", {
                error_message: message.slice(0, 200),
                ...getPageContext(),
              });
              const args = lastVipCheckoutArgsRef.current;
              if (args) {
                void startVipMembershipVideoCheckout({
                  ...args,
                  preferEmbedded: false,
                  // El intento ya se trackeó en el click original (initiated /
                  // template_used / markDualCtaConverted); la sesión hosted nueva
                  // es intencional (el usuario necesita un checkout), solo se
                  // suprime el tracking duplicado.
                  onIntent: undefined,
                });
              }
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
